import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import StatusModal  from './StatusModal';
import { useTranslation } from 'react-i18next';
import StatusHistoryModal  from './StatusHistoryModal';
import UniversalLookup from './UniversalLookup';
import ClassificationTree from './ClassificationTree';
import AttachmentsModal from './AttachmentsModal';
import AssetPreviewCard from './AssetPreviewCard';
import { useFontSize } from './FontSizeContext';
import SmartPrintButton from './PrintButton'; 
import WorkflowActionModal from './WorkflowActionModal';
import WorkflowHistoryModal from './WorkflowHistoryModal';
import IncidentModal from './IncidentModal'; 
import AuditHistoryModal from './AuditHistoryModal'
import WOCostsModal from './WOCostsModal';
import PurchaseInfoModal from './PurchaseInfoModal';
import SRModal from './SRModal'; 
import { usePermissions } from './usePermissions';
import CustomFieldSlot from './CustomFieldSlot';
import {
  Save, Search, Briefcase, Plus, ShieldAlert, Activity, FileText, Clock,
  History, Trash2, ArrowLeft, Zap, List, DollarSign, Tool, UserCheck, AlertTriangle, Tag, Paperclip, Calendar, ChevronLeft, ChevronRight, CheckCircle, Type, Minus, Printer, BrainCircuit, Filter, Workflow
} from 'lucide-react';

const WorkOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [fieldMaps, setFieldMaps] = useState({});
  const isNew = !id || id === 'new';
  const [activeTab, setActiveTab] = useState('Work Order');
  const [planSubTab, setPlanSubTab] = useState('Tasks');
  const [actualSubTab, setActualSubTab] = useState('Labor');
  // 🎯 باجينيشن جداول التابات (مهام/عمالة/مواد...) - 5 صفوف بس في الصفحة بدل
  // ما أمر شغل فيه عشرات المهام يفرد الصفحة كلها. الصفحة لكل جدول متخزنة بمفتاحه
  const [rowPages, setRowPages] = useState({});
  useEffect(() => { setRowPages({}); }, [id]);
  const setRowPage = useCallback((key, page) => setRowPages(prev => ({ ...prev, [key]: page })), []);
  // بيرجّع صفوف الصفحة الحالية بالإندكس الحقيقي في الليستة الكاملة، عشان
  // updateRow/removeRow يعدّلوا الصف الصح
  const pagedRows = (key, list) => {
    const rows = list || [];
    const pages = Math.max(1, Math.ceil(rows.length / TAB_ROWS_PER_PAGE));
    const start = (Math.min(rowPages[key] || 1, pages) - 1) * TAB_ROWS_PER_PAGE;
    return rows.slice(start, start + TAB_ROWS_PER_PAGE).map((row, i) => ({ row, idx: start + i }));
  };
  // الصف الجديد بيتضاف في الآخر - نروح لآخر صفحة عشان اليوزر يشوفه (بيتقص لعدد الصفحات الفعلي)
  const showLastRowPage = (key) => setRowPage(key, Number.MAX_SAFE_INTEGER);
  const rowPager = (key, list) => (
    <RowPager total={(list || []).length} page={rowPages[key] || 1} onPage={p => setRowPage(key, p)} isRTL={isRTL} />
  );
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [isWFHistoryOpen, setIsWFHistoryOpen] = useState(false);
  const [hoveredAsset, setHoveredAsset] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const previewTimerRef = useRef(null);
  const containerRef = useRef(null);
  const [count, setCount] = useState(0); // ??? ????????
  const location = useLocation(); 
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const changeFontSize = fontContext?.changeFontSize || (() => {});
  const [failureLists, setFailureLists] = useState({ problems: [], causes: [], remedies: [] });
  const [wfContext, setWfContext] = useState({ can_start: false, can_action: false });
  const { canCreate, canUpdate, canDelete } = usePermissions('workorders');
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showWOCostsModal, setShowWOCostsModal] = useState(false);
  const [showPurchaseInfoModal, setShowPurchaseInfoModal] = useState(false);
  const [showSRModal, setShowSRModal] = useState(false); 
  const [showAudit, setShowAudit] = useState(false);

  const FontControls = () => {
    const { fontSize, changeFontSize } = useFontSize();
  
    return (
      <div className="flex items-center gap-2 bg-surface-card/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => changeFontSize(-1)} className="hover:text-status-warning transition-colors">
          <Minus size={14} />
        </button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center">
          <Type size={14} />
          <span className="font-black text-[10px]">{fontSize}</span>
        </div>
        <button onClick={() => changeFontSize(1)} className="hover:text-status-warning transition-colors">
          <Plus size={14} />
        </button>
      </div>
    );
  };

  const [nav, setNav] = useState({ 
    first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 
  });

  const [taskStatusConfig, setTaskStatusConfig] = useState({ 
    isOpen: false, 
    taskid: null, 
    currentStatus: '',
    index: null 
  });  
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const currency = session.currency || 'EGP';
    
  const [data, setData] = useState({
    wonum: '', description: '', status: 'WAPPR', wo_type: 'CM', location_id: '', location_description: '', assetnum: '', safetypnum : '',
    asset_description: '', site_id: sessionSite, org_id: sessionOrg, supervisor: '',
    start_measure: '', end_measure: '', measure_unit: '', y_offset: '', is_linear: false,
    targstartdate: '', targcompdate: '', scheddate: '', schedfinish: '',  earlystart: '', latefinish: '',
    actstart: '', actfinish: '', slanum: '',
    tasks: [], planned_labor: [], planned_materials: [],
    actual_labor: [], actual_materials: [], failure_reports: [], assignments: [],actual_tools: [], 
    actual_services: [], safety_hazards: [], safety_precautions: [], loto_steps: [], wofailure: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '' });

  const loadWO = useCallback(async () => {

    try {
          const [woMaps, meterMaps] = await Promise.all([
              axios.get('/api/admin/field-map/workorders'),
              axios.get('/api/admin/field-map/Meters')
          ]);
          setFieldMaps({
              ...(woMaps.data || {}),
              ...(meterMaps.data || {}) 
          });    
      if (isNew) return;
      const res = await axios.get(`/api/workorder/${encodeURIComponent(id)}`);
      setData(res.data);
    } catch (err) { toast.error(t('wo.messages.loadError')); }
  }, [id, isNew]);

  useEffect(() => {
    if (isNew && data.org_id) {
        setData(prev => ({
          ...prev,
          description: '',
          status: 'WAPPR',
          site_id: session.site_id || '',
          org_id: session.org_id || '',
          requestedby: session.username || '',
          location_id: '',
          duration: 0.0,
          wo_type: 'CM', assetnum: '', safetypnum : '',
          asset_description: '', site_id: sessionSite, org_id: sessionOrg, supervisor: '',
          targstartdate: '', targcompdate: '', scheddate: '', schedfinish: '',  earlystart: '', latefinish: '',
          actstart: '', actfinish: '', failure_class: '',
          tasks: [], planned_labor: [], planned_materials: [], wofailure: [], wohazards: [], woloto: [],
          actual_labor: [], actual_materials: [], failure_reports: [], assignments: [],actual_tools: [], actual_services: [], safety_hazards: [], safety_precautions: [], loto_steps: []
          
        }));         
        axios.get(`/api/wo/next-number/${data.org_id}`)
            .then(res => {
                setData(prev => ({ ...prev, wonum: res.data.next_wonum }));
            });
    }
  }, [isNew, data.org_id]);
  useEffect(() => { loadWO(); }, [loadWO]);
  const isReadOnly = data.status === 'APPROVED' || data.status === 'APPR' || data.status === 'CLOSE' || data.status === 'CAN' || data.status === 'CLOSED' || data.status === 'COMP';
  const isEditable = !isReadOnly && (isNew ? canCreate : canUpdate);

  const loadNavigation = useCallback(async () => {
    if (isNew) return;
    
    const token = localStorage.getItem('token');
    const searchParams = new URLSearchParams(location.search); 

    const columnFilterSite = searchParams.get('site_id');
    const isGlobal = searchParams.get('all_sites') === 'true';

    try {
      const res = await axios.get(`/api/workorder/${encodeURIComponent(id)}/navigation`, {
        params: {
          // 1. بارامترات النطاق
          all_sites: isGlobal,
          site_id: sessionSite, // سيشن اليوزر الحالية
          org_id: sessionOrg,
          
          // 2. بارامترات الفلترة (عشان النافيجيشن يلتزم بنفس نتايج البحث)
          filter_site: columnFilterSite, // 🚀 هنا الربط اللي كان ناقص
          searchTerm: searchParams.get('searchTerm'),
          status: searchParams.get('status'),
          wonum: searchParams.get('wonum'),
          description: searchParams.get('description'),
          assetnum: searchParams.get('assetnum'),
          location_id: searchParams.get('location_id')
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    } catch (err) { 
      console.error("Nav load failed", err);
      setNav({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
    }
  }, [id, isNew, location.search, sessionSite, sessionOrg]);

  const fetchCount = useCallback(async () => {
    if (!data.wonum || isNew) {
        setCount(0);
        return;
    }
    try {
      const res = await axios.get(`/api/attachments/count/WO/${data.wonum}`);
      setCount(res.data.count || 0);
    } catch (err) { 
        console.error("Error fetching attachment count:", err); 
    }
  }, [data.wonum, isNew]);  
  
  const closePreview = useCallback(() => {
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    setHoveredAsset(null);
  }, []);

  // 3. دالة الدخول (فتح البريفيو)
  const handleMouseEnter = async (e, assetnum) => {
    if (!assetnum) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    
    setMousePos({ x: e.clientX, y: e.clientY });
    try {
      const res = await axios.get(`/api/getassets/${assetnum}`);
      setHoveredAsset(res.data);
    } catch (err) { console.error("Asset info not found"); }
  };

  // 4. دالة الخروج (بتايم أوت قصير للأمان)
  const handleMouseLeave = () => {
    previewTimerRef.current = setTimeout(() => {
      setHoveredAsset(null);
    }, 100); // 100ms كافية جداً
  };

  // 5. 🎯 الحل السحري: مراقبة النقر (Click) وحركة الماوس العالمية
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // لو المستخدم داس في أي حتة، اقفل البريفيو فوراً
      closePreview();
    };

    const handleGlobalMouseMove = (e) => {
      // لو البريفيو ظاهر، بنعمل فحص مسافة
      if (hoveredAsset) {
        const dist = Math.sqrt(
          Math.pow(e.clientX - mousePos.x, 2) + 
          Math.pow(e.clientY - mousePos.y, 2)
        );
        // لو الماوس ابتعد أكتر من 300 بيكسل عن مكان ظهوره الأصلي، اقفله (Safety Valve)
        if (dist > 350) closePreview();
      }
    };

    window.addEventListener('mousedown', handleGlobalClick);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    
    return () => {
      window.removeEventListener('mousedown', handleGlobalClick);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [hoveredAsset, mousePos, closePreview]);

  useEffect(() => {
    const openLogModal = () => {
      // فتح الـ Modal (يمكنك استخدام state للتحكم في ظهوره)
      setShowAudit(true);
    };
  
    window.addEventListener('GLOBAL_LOG_HANDLER', openLogModal);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLogModal);
  }, []);

  useEffect(() => {
    if (isNew || !data.wonum) return;
    const handleIncident = () => setShowIncidentModal(true);
    const handleSR = () => setShowSRModal(true);

    window.addEventListener('OPEN_INCIDENT_MODAL', handleIncident);
    window.addEventListener('OPEN_SR_MODAL', handleSR);
    return () => {
      window.removeEventListener('OPEN_INCIDENT_MODAL', handleIncident);
      window.removeEventListener('OPEN_SR_MODAL', handleSR);
    };
  }, [isNew, data.wonum]);

  // 🎯 استماع لأحداث التحليلات المالية والمشتريات
  useEffect(() => {
    if (isNew || !data.wonum) return;
    const handleWOCosts = () => setShowWOCostsModal(true);
    const handlePOInfo = () => setShowPurchaseInfoModal(true);

    window.addEventListener('OPEN_WO_COSTS', handleWOCosts);
    window.addEventListener('OPEN_PO_INFO', handlePOInfo);
    return () => {
      window.removeEventListener('OPEN_WO_COSTS', handleWOCosts);
      window.removeEventListener('OPEN_PO_INFO', handlePOInfo);
    };
  }, [isNew, data.wonum]);
  
  const handleTaskStatusClick = (row, idx) => {

    if (!row.taskid) {
      const nextStatus = row.status === 'WAPPR' ? 'APPR' : 'WAPPR';
      updateRow('tasks', idx, 'status', nextStatus);
      updateRow('tasks', idx, 'status', nextStatus);
      toast.success(t('wo.messages.taskStatusLocal'));
      return;
    }
    
    setTaskStatusConfig({
      isOpen: true,
      taskid: row.taskid,
      currentStatus: row.status,
      index: idx
    });
  };
  
  const handleCreateMR = async (materialItem) => {
    // 1. Validation: Ensure item and store are selected
    if (!materialItem.itemnum || !materialItem.fromstore) {
      return toast.error(t('wo.messages.selectItemStore'));
    }
  
    // 2. Local check: Avoid duplicate requests for the same item
    const isAlreadyRequested = data.related_mrs?.some(
      mr => mr.description.includes(materialItem.itemnum) && mr.status !== 'CAN'
    );
  
    if (isAlreadyRequested) {
      const confirm = await Swal.fire({
        ...swalTheme(),
        title: t('wo.messages.duplicateReqTitle'),
        text: t('wo.messages.duplicateReqText', { item: materialItem.itemnum }),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonColor: swalDangerColor(),
        confirmButtonText: t('wo.actions.yesCreate'),
        cancelButtonText: t('common.cancel')
      });
      if (!confirm.isConfirmed) return;
    }
  
    const loadId = toast.loading(t('wo.messages.creatingMR'));
  
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');

      const payload = {
        wonum: data.wonum,
        itemnum: materialItem.itemnum,
        quantity: materialItem.quantity,
        fromstore: materialItem.fromstore,
        site_id: data.site_id,
        org_id: data.org_id,
        description: `MR for WO ${data.wonum}: ${materialItem.itemnum}`
      };
  
      // Direct API call to create MR
      const res = await axios.post('/api/workorder/create-direct-mr', payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.data.status === 'success') {
        toast.success(t('wo.messages.mrCreated', { num: res.data.mrnum }), { id: loadId });
        
        // 🎯 Update state immediately to reflect changes in UI
        setData(prev => ({
          ...prev,
          related_mrs: [...(prev.related_mrs || []), res.data.new_mr],
          status: 'WMAT' // Auto-change WO status to Waiting for Material
        }));
      }
    } catch (err) {
        toast.error(err.response?.data?.detail || t('wo.messages.mrFail'), { id: loadId });
    }
  };

  // 🎯 طلب شراء لخدمة خارجية - نفس منطق handleCreateMR بالظبط، بس السطر
  // من نوع SERVICE (مالوش itemnum ولا رصيد مخزن) وبيرجع مربوط بصف الخدمة
  // نفسه (wasid) عشان الفرونت يعرف إن الطلب اتفتح ويمنع تكرار الضغط
  const handleCreateServicePR = async (serviceItem, idx) => {
    if (!serviceItem.description || !serviceItem.vendor) {
      return toast.error(t('wo.messages.selectServiceVendor'));
    }

    const loadId = toast.loading(t('wo.messages.creatingMR'));

    try {
      const token = localStorage.getItem('token');

      const payload = {
        wonum: data.wonum,
        linetype: 'SERVICE',
        wasid: serviceItem.wasid,
        vendor: serviceItem.vendor,
        unitcost: serviceItem.linecost,
        site_id: data.site_id,
        org_id: data.org_id,
        description: `${t('wo.tables.serviceDesc')} - WO ${data.wonum}: ${serviceItem.description}`
      };

      const res = await axios.post('/api/workorder/create-direct-mr', payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (res.data.status === 'success') {
        toast.success(t('wo.messages.mrCreated', { num: res.data.mrnum }), { id: loadId });

        setData(prev => {
          const newList = [...prev.actual_services];
          newList[idx].mrnum = res.data.mrnum;
          return {
            ...prev,
            actual_services: newList,
            related_mrs: [...(prev.related_mrs || []), res.data.new_mr],
            status: 'WMAT'
          };
        });
      }
    } catch (err) {
        toast.error(err.response?.data?.detail || t('wo.messages.mrFail'), { id: loadId });
    }
  };
  
  const handleWOStatusAction = async (targetStatus, actionLabel) => {
    // 1. رسالة تأكيد احترافية باستخدام SweetAlert2
    const result = await Swal.fire({
      ...swalTheme(),
      title: `${t('common.confirm')} ${actionLabel}`,
      text: t('wo.messages.confirmStatusChange', { wonum: data.wonum, status: targetStatus }),
      icon: targetStatus === 'CAN' ? 'warning' : 'question',
      showCancelButton: true,
      ...(targetStatus === 'CAN' ? { confirmButtonColor: swalDangerColor() } : {}),
      confirmButtonText: t('wo.actions.yesChange'),
      cancelButtonText: t('common.cancel')
    });
  
    if (!result.isConfirmed) return;
  
    const loadId = toast.loading(t('wo.messages.actionInProgress', { action: actionLabel }));
  
    try {
      const token = localStorage.getItem('token');
      
      // 2. نداء الباكيند (نفس الدالة اللي كتبناها سوا)
      const res = await axios.post(
        `/api/workorder/${encodeURIComponent(data.wonum)}/change-status`, 
        { new_status: targetStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
  
      if (res.data.status === 'success') {
        toast.success(t('wo.messages.statusSuccess'), { id: loadId });
        loadWO();
        
        // 3. تحديث البيانات في الشاشة فوراً عشان الأزرار تتغير أو تختفي
        setData(prev => ({ 
          ...prev, 
          status: targetStatus,
          // تحديث التواريخ لو الباكيند رجعها
          actual_start: res.data.actual_start || prev.actual_start,
          actual_finish: res.data.actual_finish || prev.actual_finish
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('wo.messages.statusFail'), { id: loadId });
    }
  };
  
  const handleSave = async () => {
    const loadId = toast.loading(t('wo.messages.saving'));
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workorder/save', data, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(t('common.save_success'), { id: loadId });
            navigate(`/workorders/${encodeURIComponent(data.wonum)}${location.search}`, { replace: true });
    } catch (err) { toast.error(err.response?.data?.detail || t('common.saveFail'), { id: loadId }); }
  };

  const handleDelete = async () => {
        // رسالة تأكيد بنمط ماكسيمو
        const result = await Swal.fire({
            ...swalTheme(),
            title: t('common.are_you_sure'),
            text: t('wo.messages.deleteConfirm', { wonum: data.wonum }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: swalDangerColor(),
            confirmButtonText: t('common.delete'),
            cancelButtonText: t('common.cancel'),
            customClass: { popup: `${isRTL ? 'font-cairo' : 'font-sans'} text-[12px]` }
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`/api/workorder/${encodeURIComponent(data.wonum)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
              toast.success(t('common.delete_success'));
                navigate('/workorders'); // العودة للقائمة بعد الحذف
            } catch (err) {
              toast.error(err.response?.data?.detail || t('common.delete_fail'));
            }
        }
    };

  const handleApplySLA = async () => {
      // 1. التأكد إن أمر الشغل محفوظ (له ID)
      if (isNew) {
          return toast.error(t('wo.messages.saveBeforeSLA'));
      }
      if (data.slanum) {
          return toast.error(t('wo.messages.slaExists'));
      }
      const tid = toast.loading(t('wo.messages.matchingSLA'));
      try {
          const token = localStorage.getItem('token');
          // 2. نداء الـ API اللي إنت لسه كاتبه في الباكيند
          const res = await axios.post(`/api/system/apply-sla-to-wo`, null, {
              params: { wo_id: data.wo_id }, // بنبعت الـ ID كـ Query Param
              headers: { 'Authorization': `Bearer ${token}` }
          });

          if (res.data === true) {
              toast.success(t('wo.messages.slaApplied'), { id: tid });
              // 3. تحديث البيانات في الشاشة عشان الـ Widget والتواريخ تظهر
              loadWO(); 
          } else {
              toast.error(t('wo.messages.noMatchingSLA'), { id: tid });
          }
      } catch (err) {
          toast.error(t('wo.messages.slaError'), { id: tid });
      }
  };

  // 🎯 localData: قايمة جاهزة في الميموري (زي failureLists.problems) بدل
  // ما الـ UniversalLookup يروح يجيب بيانات من سيرفر - مفيدة للقوائم
  // الصغيرة المحسوبة أصلاً على الفرونت (شوف الدعم في UniversalLookup.js)
  const handleSmartSearch = (fieldName, title, defaultType, onCustomSelect = null, localData = null) => {
    const linkedDomain = (fieldMaps || {})[fieldName];
    setLookup({
      isOpen: true,
      title,
      type: linkedDomain ? 'DOMAIN' : defaultType,
      domainId: linkedDomain || '',
      field: fieldName,
      onCustomSelect: onCustomSelect,
      localData: localData
    });
  };
  
  const handleSearch = (fieldName, title, defaultType, lineIdx = null) => {
    let extraFilters = { site_id: data.site_id, org_id: data.org_id };
    if (fieldName === 'ref_mrnum') extraFilters.status = 'APPR';
    if (fieldName === 'assetnum') extraFilters.location = data.location_id;
    // 🎯 اختيار وحدة أصل دوّار بعينها لأداة معينة على أمر الشغل - نفلتر
    // بنفس رقم الأداة (toolnum) عشان الليست تعرض بس وحدات الأداة دي
    // (والمتاحة، مش المتسحوبة فعلاً - شوف /lookups/assets في main.py)
    if (fieldName === 'row_act_asset' && lineIdx !== null) extraFilters.itemnum = data.actual_tools?.[lineIdx]?.toolnum;

    setLookup({
      isOpen: true,
      title: title,
      type: defaultType,
      field: fieldName,
      filters: extraFilters,
      targetIdx: lineIdx
    });
  };

  const fetchSafetyPlanDetails = async (spNum) => {
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/safety-plans/${spNum}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // تحديث الداتا بجداول السيفتي (Hazards, LOTO)
        setData(prev => ({
            ...prev,
            wohazards: res.data.hazards || [],
            woloto: res.data.woloto || []
        }));
    } catch (err) {
        console.error("Failed to fetch safety plan details", err);
    }
  };

  // 🎯 بس بيجيب قوائم الأكواد (Problems/Causes/Remedies) بتاعة الـ Failure
  // Class عشان قوائم البحث في تاب Failure Reporting - من غير ما يلمس
  // data.wofailure خالص. آمن يتنادى في أي وقت (حتى عند فتح أمر شغل قديم
  // ليه failure_class محفوظة بالفعل)، بعكس fetchFailureTree تحت اللي
  // بتستبدل wofailure بالكامل - مصممة بس للحظة اختيار أصل جديد يدويًا
  const loadFailureLists = async (className) => {
    if (!className) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/failure-classes-by-name/${className}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFailureLists({
        problems: res.data.problems || [],
        causes: res.data.causes || [],
        remedies: res.data.remedies || []
      });
      return res.data;
    } catch (err) {
      console.error("Failure lists load failed", err);
      return null;
    }
  };

  // 🎯 لما اليوزر يفتح أمر شغل ليه failure_class متسجلة بالفعل (من
  // قاعدة البيانات أو من التوريث التلقائي وقت الإنشاء)، قوائم البحث
  // (failureLists) كانت بتفضل فاضية تمامًا لأن الدالة الوحيدة اللي بتملاها
  // (fetchFailureTree) كانت بتتنفذ بس لحظة ما اليوزر يختار أصل جديد يدويًا
  // من شاشة الأمر نفسه - يعني تاب Failure Reporting كان بيعرض "Current
  // Failure Class: X" صح، بس أي محاولة لاختيار Problem/Cause/Remedy
  // بترجع "No matching records found" رغم إن الأكواد موجودة فعليًا
  useEffect(() => {
    if (data.failure_class) {
      loadFailureLists(data.failure_class);
    }
  }, [data.failure_class]);

  const fetchFailureTree = async (className) => {
  if (!className) return;
  try {
    const res_data = await loadFailureLists(className);
    if (!res_data) return;
    const res = { data: res_data };

    // 🎯 حقن أول مشكلة وأول سبب أوتوماتيك في جدول الفيلر
    const autoFailures = [];
    
    if (res.data.problems && res.data.problems.length > 0) {
      autoFailures.push({
        failure_type: 'PROBLEM',
        failure_code: res.data.problems[0].code,
        description: res.data.problems[0].description
      });
    }
    
    if (res.data.causes && res.data.causes.length > 0) {
      autoFailures.push({
        failure_type: 'CAUSE',
        failure_code: res.data.causes[0].code,
        description: res.data.causes[0].description
      });
    }

    // تحديث الحالة لتبدأ بالبيانات دي
    setData(prev => ({ ...prev, wofailure: autoFailures }));
    
    toast.success(t('wo.messages.failureTreeLoaded', { class: className }));
  } catch (err) {
    console.error("Failure Tree load failed", err);
  }
};
  
  const addFailureRow = (type) => {
    const newRow = { failure_type: type, failure_code: '', description: '' };
    setData(prev => ({ ...prev, wofailure: [...(prev.wofailure || []), newRow] }));
  };

  const updateFailureRow = (idx, field, value, description = '') => {
    const newList = [...data.wofailure];
    newList[idx] = { ...newList[idx], [field]: value, description: description };
    setData(prev => ({ ...prev, wofailure: newList }));
  };

  // 🎯 FailureTable مالوش onRemove مبعوت له خالص - زرار الحذف كان بينادي
  // undefined(i) ويكسر الشاشة بمجرد الدوس عليه
  const removeFailureRow = (idx) => {
    const newList = data.wofailure.filter((_, i) => i !== idx);
    setData(prev => ({ ...prev, wofailure: newList }));
  };

  const handleLookupSelect = (row) => {
    // 🎯 مسار عام لأي نداء بـ handleSmartSearch(..., onCustomSelect) - كان
    // متخزّن في lookup.onCustomSelect بس مش بينده عليه حد خالص، فأي حقل
    // بيعتمد عليه (زي أكواد الفشل تحت) كان شكله بيفتح المودال ويختار بس
    // فعليًا مايحصلش أي تحديث. لازم يتفحص الأول قبل سلسلة lookup.field
    // العادية عشان يوقف هنا ومايكملش على المسار العام
    if (typeof lookup.onCustomSelect === 'function') {
      lookup.onCustomSelect(row);
      return;
    }
    const val = row.taxid || row.mrnum || row.safetypnum || row.laborcode || row.craft_code || row.value || row.assetnum || row.location_id || row.id || row.itemnum  || row.toolnum || row.comp_num ;
    if (lookup.field === 'row_plan_craft' && lookup.targetIdx !== null) {
        updateRow('planned_labor', lookup.targetIdx, 'craft', row.craft_code || row.value);
      } 
    // في قسم الـ Plans - Labor
    else if (lookup.field === 'row_plan_labor' && lookup.targetIdx !== null) {
        updateRow('planned_labor', lookup.targetIdx, 'laborcode', row.laborcode || row.value);
        updateRow('planned_labor', lookup.targetIdx, 'laborname', row.name || row.description); // 👈 إضافة لسحب اسم العامل
        if(row.craft_code) updateRow('planned_labor', lookup.targetIdx, 'craft', row.craft_code);
        if(row.actual_rate) updateRow('planned_labor', lookup.targetIdx, 'laborrate', row.actual_rate);
    }
    else if(lookup.field === 'row_plan_item' && lookup.targetIdx !== null){
        updateMaterialRow(lookup.targetIdx, 'itemnum', row.itemnum || row.value);
        if(row.description) updateMaterialRow(lookup.targetIdx, 'description', row.description);
        if (row.avgcost || row.lastcost) {
            updateMaterialRow(lookup.targetIdx, 'unitcost', row.avgcost || row.lastcost);
        }
    }
    else if(lookup.field === 'row_plan_tool' && lookup.targetIdx !== null){
        updateRow('planned_tools', lookup.targetIdx, 'toolnum', row.toolnum || row.value);
        if(row.description) updateRow('planned_tools', lookup.targetIdx, 'description', row.description);
        if(row.toolrate) updateRow('planned_tools', lookup.targetIdx, 'toolrate', row.toolrate);
    }
    else if(lookup.field === 'row_plan_store' && lookup.targetIdx !== null){
        updateMaterialRow(lookup.targetIdx, 'fromstore', row.location || row.value);
    
    }
    // أضف هذه الشروط داخل handleLookupSelect
    else if (lookup.field === 'row_assign_craft' && lookup.targetIdx !== null) {
        updateAssignmentRow(lookup.targetIdx, 'craft', row.craft_code || row.value);
    } 
    else if (lookup.field === 'row_assign_labor' && lookup.targetIdx !== null) {
        updateAssignmentRow(lookup.targetIdx, 'laborcode', row.laborcode || row.value);
        // سحب المهنة والمعدل أوتوماتيك من العامل المختار
        if(row.craft_code) updateAssignmentRow(lookup.targetIdx, 'craft', row.craft_code);
        if(row.actual_rate) updateAssignmentRow(lookup.targetIdx, 'laborrate', row.actual_rate);
    }
           
    // في قسم الـ Actuals - Labor
    else if (lookup.field === 'row_act_labor' && lookup.targetIdx !== null) {
        updateActualLaborRow(lookup.targetIdx, 'laborcode', row.laborcode || row.value);
        updateActualLaborRow(lookup.targetIdx, 'laborname', row.name || row.description); // 👈 إضافة لسحب اسم العامل
        if(row.actual_rate) updateActualLaborRow(lookup.targetIdx, 'laborrate', row.actual_rate);
    }
    // في قسم الـ Actuals - Material
    else if (lookup.field === 'row_act_item' && lookup.targetIdx !== null) {
        updateActualMaterialRow(lookup.targetIdx, 'itemnum', row.itemnum || row.value);
        updateActualMaterialRow(lookup.targetIdx, 'description', row.description); // 👈 إضافة لسحب وصف الصنف
        if(row.avgcost) updateActualMaterialRow(lookup.targetIdx, 'unitcost', row.avgcost);
    }  
    else if (lookup.field === 'row_act_tool' && lookup.targetIdx !== null) {
        const newList = [...data.actual_tools];
        newList[lookup.targetIdx].toolnum = row.toolnum || row.value;
        newList[lookup.targetIdx].description = row.description || '';
        newList[lookup.targetIdx].toolrate = row.toolrate || 0;
        newList[lookup.targetIdx].linecost = (newList[lookup.targetIdx].hours || 0) * (row.toolrate || 0);
        // 🎯 لو غيّر الأداة، أي وحدة أصل كانت متختارة قبل كده بقت غير صالحة
        newList[lookup.targetIdx].assetnum = '';
        setData({ ...data, actual_tools: newList });
    }
    else if (lookup.field === 'row_act_asset' && lookup.targetIdx !== null) {
        const newList = [...data.actual_tools];
        newList[lookup.targetIdx].assetnum = row.assetnum || row.value;
        newList[lookup.targetIdx].asset_status = 'CHECKED OUT'; // هيتقفل فعليًا وقت الحفظ
        setData({ ...data, actual_tools: newList });
    }
    else if (lookup.field === 'row_act_service_vendor' && lookup.targetIdx !== null) {
        const newList = [...data.actual_services];
        newList[lookup.targetIdx].vendor = row.comp_num || row.value;
        newList[lookup.targetIdx].vendor_name = row.name || row.description || '';
        setData({ ...data, actual_services: newList });
    }
    else if (lookup.field === 'row_act_store' && lookup.targetIdx !== null) {
        const selectedStore = row.location || row.value;
        const currentItem = data.actual_materials[lookup.targetIdx]?.itemnum;
    
        const newList = [...data.actual_materials];
        newList[lookup.targetIdx].fromstore = selectedStore;
        setData({ ...data, actual_materials: newList });
    
        if (currentItem) {
            // نبعت نجيب السعر والرصيد في نفس الطلب
            axios.get(`/api/inventory/details/${selectedStore}/${currentItem}`)
                .then(res => {
                    const { avgcost, curbal } = res.data;
                    
                    // تحديث السعر
                    updateActualMaterialRow(lookup.targetIdx, 'unitcost', avgcost || 0);
    
                    // التحقق من الرصيد
                    if (!curbal || curbal <= 0) {
                        toast.error(`${t('wo.messages.stocknobalance')} ${selectedStore}`, {
                            duration: 5000,
                            style: { border: '1px solid var(--status-danger)', padding: '16px', color: 'var(--status-danger)' }
                        });
                    } else {
                        toast.success(`تم جلب السعر: ${avgcost} - الرصيد المتاح: ${curbal}`);
                    }
                })
                .catch(() => toast.error("فشل في الاتصال بسجلات المخزن"));
        }
    }             
        if (lookup.field === 'ref_mrnum') {
      fetchAndFillMR(val);
    } else if (lookup.field === 'tax_id') {
      const newLines = [...data.lines];
      const idx = lookup.targetIdx;
      newLines[idx].tax_id = row.taxid;
      newLines[idx].taxrate = row.taxrate || 0;
      recalculateTotals(newLines);
    } else if (lookup.field === 'itemnum' && lookup.targetIdx !== null) {
      const newLines = [...data.lines];
      const idx = lookup.targetIdx;
      newLines[idx].itemnum = row.itemnum;
      newLines[idx].description = row.description;
      newLines[idx].unitcost = row.avgcost || 0;
      if (row.tax_id) {
          newLines[idx].tax_id = row.tax_id;
          newLines[idx].taxrate = row.taxrate || 0;
      }
      recalculateTotals(newLines);
    } else if (lookup.field === 'location_id') {
      // 🎯 لازم كيس مخصص هنا - الـ val العام فوق بيدور على row.safetypnum
      // قبل row.location_id، والمواقع بترجع ليها safetypnum (خطة السلامة
      // الافتراضية) فكانت بتتكتب في location_id بالغلط بدل كود الموقع
      // نفسه. ده كان بيكسر فلترة الأصول بالموقع كمان لأنها بتعتمد على
      // data.location_id الصحيح (شوف handleSearch فوق)
      setData(prev => ({
        ...prev,
        location_id: row.location_id || row.value,
        location_description: row.description || ''
      }));
    } else if (lookup.field === 'assetnum') {
      // 🎯 تحديث بيانات الأصل + الفيلر كلاس + السيفتي بلان
      setData(prev => ({
        ...prev,
        assetnum: row.assetnum,
        asset_description: row.description,
        location_id: row.location_id || prev.location_id,
        failure_class: row.failure_class || '', // سحب الفيلر كلاس من موديل الـ Asset
        safetypnum: row.safetypnum || '',        // سحب السيفتي بلان من موديل الـ Asset
        is_linear: row.is_linear || false,
        measure_unit: row.measure_unit || 'KM'
      }));

      // 🚀 إطلاق صاروخ الفيتش للسيفتي بلان فوراً
      if (row.safetypnum) {
        fetchSafetyPlanDetails(row.safetypnum);
      } else {
        // تصفير تابة السيفتي لو مفيش بلان
        setData(prev => ({ ...prev, wohazards: [], woloto: [] }));
      }
      if (row.failure_class) {
        fetchFailureTree(row.failure_class);
      }
      
      toast.success(`Asset Linked: ${row.assetnum}`);
    } 
    // شرط الفايلر كودز المختار يدوياً (لو حبيت تختارهم في تابة الفايلر)
    else if (['problem_code', 'cause_code', 'remedy_code'].includes(lookup.field)) {
      // lookup.targetIdx ده لازم نكون باعتينه من الـ FailureTable
      const targetIdx = lookup.targetIdx; 
      
      if (targetIdx !== null && targetIdx !== undefined) {
        const newList = [...data.wofailure];
        newList[targetIdx] = { 
          ...newList[targetIdx], 
          failure_code: row.code, 
          description: row.description 
        };
        
        setData(prev => ({ ...prev, wofailure: newList }));
      }
    } else {
      setData({ ...data, [lookup.field]: val });
    }
    setLookup({ ...lookup, isOpen: false });
  };
  
  // Add tools & services to Actuals
  const addRow = (listName) => {
      let newRow = {};
      if (listName === 'actual_labor') {
          newRow = { laborcode: '', startdate: '', finishdate: '', regularhrs: 0, laborrate: 0, linecost: 0 };
      } else if (listName === 'actual_materials') {
          newRow = { itemnum: '', fromstore: '', quantity: 1, unitcost: 0, linecost: 0 };
      } else if (listName === 'actual_tools') {
          newRow = { toolnum: '', description: '', assetnum: '', hours: 0, toolrate: 0, linecost: 0 };
      } else if (listName === 'actual_services') {
          newRow = { description: '', vendor: '', linecost: 0 };
      }
      setData(prev => ({ ...prev, [listName]: [...(prev[listName] || []), newRow] }));
      showLastRowPage(listName);
  };
  // Tab (Assignments)
  const addAssignmentRow = () => {
    const newRow = { 
      description: '', 
      status: 'ASSIGNED', 
      craft: '', 
      laborcode: '', 
      quantity: 1, 
      laborhrs: 0.0, 
      laborrate: 0.0, 
      linecost: 0.0 
    };
    setData({ ...data, assignments: [...data.assignments, newRow] });
    showLastRowPage('assignments');
  };
  
  // دالة التحديث الذكي للتكليفات (لحساب التكاليف فوراً)
  const updateAssignmentRow = (idx, field, value) => {
    const newList = [...data.assignments];
    newList[idx][field] = value;
  
    // إعادة حساب التكلفة لو تغيرت الساعات أو المعدل أو الكمية
    if (['quantity', 'laborhrs', 'laborrate'].includes(field)) {
      const row = newList[idx];
      newList[idx].linecost = (row.quantity || 0) * (row.laborhrs || 0) * (row.laborrate || 0);
    }
    setData({ ...data, assignments: newList });
  };  
  // --- ???? ?????? (Tasks) ---
  const addTaskRow = () => {
    const newRow = { 
      wosequence: (data.tasks.length + 1) * 10, 
      description: '', 
      estdur: 1.0, 
      status: 'WAPPR' 
    };
    setData({ ...data, tasks: [...data.tasks, newRow] });
    showLastRowPage('tasks');
  };
  
  // ---  (Planned Labor) ---
  const addLaborRow = () => {
    const newRow = { 
      taskid: null, craft: '', laborcode: '',
      quantity: 1, laborhrs: 0, laborrate: 0, linecost: 0 
    };
    setData({ ...data, planned_labor: [...data.planned_labor, newRow] });
    showLastRowPage('planned_labor');
  };
  
  // --- ???? ????? ?????? ---
  const removeRow = (listName, index) => {
    const newList = [...data[listName]];
    newList.splice(index, 1);
    setData({ ...data, [listName]: newList });
  };

    
  // ---  (Smart Update) ---
  const updateRow = (listName, index, field, value) => {
    const newList = [...data[listName]];
    newList[index][field] = value;
  
    if (listName === 'planned_labor' && ['quantity', 'laborhrs', 'laborrate'].includes(field)) {
      const row = newList[index];
      newList[index].linecost = (row.quantity || 0) * (row.laborhrs || 0) * (row.laborrate || 0);
    }
    if ((listName === 'planned_tools' || listName === 'actual_tools') && ['hours', 'toolrate'].includes(field)) {
      const row = newList[index];
      newList[index].linecost = (row.hours || 0) * (row.toolrate || 0);
    }

    setData({ ...data, [listName]: newList });
  };
  
  // (Materials) ---
  const addMaterialRow = () => {
    const newRow = { 
      linetype: 'ITEM', itemnum: '', description: '', 
      quantity: 1, unitcost: 0, linecost: 0, fromstore: '' 
    };
    setData({ ...data, planned_materials: [...data.planned_materials, newRow] });
    showLastRowPage('planned_materials');
  };
  
  // (Tools) ---
  const addToolRow = () => {
    const newRow = {
      toolnum: '', description: '', quantity: 1,
      toolrate: 0, linecost: 0, hours: 0
    };
    setData({ ...data, planned_tools: [...(data.planned_tools || []), newRow] });
    showLastRowPage('planned_tools');
  };
  
  const updateMaterialRow = (idx, field, value) => {
    const newList = [...data.planned_materials];
    newList[idx][field] = value;
    
    if (field === 'quantity' || field === 'unitcost') {
      newList[idx].linecost = (newList[idx].quantity || 0) * (newList[idx].unitcost || 0);
    }
    setData({ ...data, planned_materials: newList });
  };  
  
  // --- تابة التنفيذ الفعلي (Actuals) ---
  
  // إضافة عامل فعلي
  const addActualLaborRow = () => {
    const newRow = { 
      laborcode: '', startdate: '', finishdate: '', 
      regularhrs: 0.0, laborrate: 0.0, linecost: 0.0 
    };
    setData({ ...data, actual_labor: [...data.actual_labor, newRow] });
  };
  
  // إضافة مواد فعلية
  const addActualMaterialRow = () => {
    const newRow = { 
      itemnum: '', quantity: 1.0, unitcost: 0.0, linecost: 0.0 
    };
    setData({ ...data, actual_materials: [...data.actual_materials, newRow] });
  };
  
  const calculateHours = (start, finish) => {
    if (!start || !finish) return 0;
    const diff = new Date(finish) - new Date(start);
    return Math.max(0, (diff / (1000 * 60 * 60)).toFixed(2)); // تحويل لكسر عشري
  };
  
  const updateActualLaborRow = (idx, field, value) => {
    const newList = [...data.actual_labor];
    newList[idx][field] = value;
  
    // ���� السحر هنا: حساب الساعات تلقائياً
    if (field === 'startdate' || field === 'finishdate') {
      const hrs = calculateHours(newList[idx].startdate, newList[idx].finishdate);
      newList[idx].regularhrs = hrs;
    }
  
    // إعادة حساب التكلفة
    newList[idx].linecost = (newList[idx].regularhrs || 0) * (newList[idx].laborrate || 0);
    setData({ ...data, actual_labor: newList });
  };
  
  const updateActualMaterialRow = (idx, field, value) => {
    const newList = [...data.actual_materials];
    newList[idx][field] = value;
    // حساب التكلفة
    if (['quantity', 'unitcost'].includes(field)) {
      newList[idx].linecost = (newList[idx].quantity || 0) * (newList[idx].unitcost || 0);
    }
    setData({ ...data, actual_materials: newList });
  };  
  
  const getPlannedTotal = () => {
    const labor = data.planned_labor?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    const materials = data.planned_materials?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    const tools = data.planned_tools?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    return labor + materials + tools;
  };
  
  const getActualTotal = () => {
    const labor = data.actual_labor?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    const materials = data.actual_materials?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    const tools = data.actual_tools?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    const services = data.actual_services?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0;
    return labor + materials + tools + services;
  };  
  
  // فحوصات ما قبل تغيير الحالة (تكليفات مفتوحة + تجاوز الميزانية) - يُستدعى من زرار "Change Status"
  // العام وكمان من اختصارات السايدبار (Complete Work / Close WO) عشان نفس الفحوصات تتطبق في الاتنين
  const runPreStatusChecks = (onProceed) => {
    const actual = getActualTotal();
    const planned = getPlannedTotal();

    // 1. فحص الأسايمنتس المفتوحة (Front-end Check)
    const openAssignments = data.assignments?.filter(a =>
      !['COMPLETED', 'CANCELED'].includes(a.status)
    ).length;

    if (openAssignments > 0) {
      return toast.error(t('wo.messages.openAssignError', { count: openAssignments }), { duration: 5000 });
    }
    // 2. لو الميزانية اتخرمت (الفعلي > المخطط)
    if (actual > planned && planned > 0) {
      toast((t) => (
        <span className="flex flex-col gap-2 text-[0.8]">
          <b className="text-status-danger uppercase">⚠️{t('wo.messages.budgetExceeded')}</b>
          <span>{t('wo.tabs.actuals')}: {actual.toLocaleString()} | {t('wo.tabs.plans')}: {planned.toLocaleString()}</span>
          <div className="flex gap-2 mt-2">
            <button
              className="bg-status-danger text-white px-3 py-1 rounded-sm font-black uppercase"
              onClick={() => {
                toast.dismiss(t.id);
                onProceed();
              }}
            >
              {t('common.confirm')}
            </button>
            <button className="border px-3 py-1" onClick={() => toast.dismiss(t.id)}>{t('common.cancel')}</button>
          </div>
        </span>
      ), { duration: 6000, position: 'top-center' });
    } else {
      onProceed();
    }
  };

  const handleStatusChangeInitiation = () => {
    runPreStatusChecks(() => setShowStatusModal(true));
  };

  const handleCompleteAssignment = async (idx) => {
    const row = data.assignments[idx];
    // 🎯 صف لسه مانزلش الداتابيز (اتضاف بس دلوقتي بـ "+ Add Row" ولسه ما
    // اتحفظش) - مفيش taskid حقيقي نقدر نحفظ بيه فورًا، لازم يحفظ الصفحة
    // كلها الأول
    if (!row.taskid) {
      toast.error(t('wo.messages.saveWoFirst', 'Save the Work Order first, then complete this assignment.'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`/api/workorder/assignments/${row.taskid}/complete`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const newList = [...data.assignments];
      newList[idx] = { ...newList[idx], ...res.data };
      setData({ ...data, assignments: newList });
      toast.success(t('wo.messages.assignComplete'), {
        icon: '✅',
        style: { borderRadius: '4px', background: '#333', color: '#fff', fontSize: '12px' }
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('wo.messages.assignCompleteFail', 'Failed to complete assignment'));
    }
  };
  
  useEffect(() => {
    const triggerAppr = () => {
        if (isNew || !data.wonum) {
          console.log("⚠️ Document is new, skipping SideMenu trigger.");
          return;
        }
        if (['APPR', 'COMP', 'CLOSE', 'CAN'].includes(data.status)) {
        return toast.error(t('wo.messages.actionNotAllowed', { status: data.status }), {
          icon: '❌',
          style: { borderRadius: '4px', background: '#333', color: '#fff', fontSize: '12px' }
        });
      }
      handleWOStatusAction('APPR', t('wo.actions.approve'));
    };
    window.addEventListener('GLOBAL_APPR_WO', triggerAppr);
    return () => window.removeEventListener('GLOBAL_APPR_WO', triggerAppr);
  }, [handleWOStatusAction, data.status]);
  
  useEffect(() => {
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const triggerCancel = () => {
        if (['APPR', 'COMP', 'CLOSE', 'CAN'].includes(data.status)) {
        return toast.error(t('wo.messages.actionNotAllowed', { status: data.status }), {
          icon: '❌',
          style: { borderRadius: '4px', background: '#333', color: '#fff', fontSize: '12px' }
        });
      }
      handleWOStatusAction('CAN', t('wo.actions.cancel'));
    };
    window.addEventListener('GLOBAL_CAN_WO', triggerCancel);
    return () => window.removeEventListener('GLOBAL_CAN_WO', triggerCancel);
  }, [handleWOStatusAction, data.status]);
  
  useEffect(() => {
    const triggerStW = () => {
      if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
      }
      // 1. التحقق لازم يكون جوه الدالة عشان يشتغل عند النداء فقط
      if (['WAPPR', 'COMP', 'CLOSE', 'CAN'].includes(data.status)) {
        return toast.error(t('wo.messages.actionNotAllowed', { status: data.status }), {
          icon: '❌',
          style: { borderRadius: '4px', background: '#333', color: '#fff', fontSize: '12px' }
        });
      }
      
      // 2. لو الحالة صح، نفذ الأكشن
      handleWOStatusAction('INPRG', t('wo.actions.startWork'));
    };

    window.addEventListener('GLOBAL_STRT_WO', triggerStW);
    return () => window.removeEventListener('GLOBAL_STRT_WO', triggerStW);
  }, [handleWOStatusAction, data.status]); 
  
    useEffect(() => {
    const triggerCloseW = () => {
      if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
      }
      // 1. التحقق لازم يكون جوه الدالة عشان يشتغل عند النداء فقط
      if (data.status !== 'COMP') {
        return toast.error(t('wo.messages.actionNotAllowed', { status: data.status }), {
          icon: '❌',
          style: { borderRadius: '4px', background: '#333', color: '#fff', fontSize: '12px' }
        });
      }
      
      // 2. لو الحالة صح، شغّل نفس فحوصات "Change Status" (تكليفات مفتوحة + تجاوز ميزانية) قبل التنفيذ
      runPreStatusChecks(() => handleWOStatusAction('CLOSE', t('wo.actions.close')));
    };

    window.addEventListener('GLOBAL_CLOSE_WO', triggerCloseW);
    return () => window.removeEventListener('GLOBAL_CLOSE_WO', triggerCloseW);
  }, [handleWOStatusAction, data.status]);
  
  useEffect(() => {
    const triggerCompW =  () => {
      //await loadWO(); // غيرتها لـ loadWO عشان تجيب الداتا الصح
    
    // 🎯 البوابة: لو السجل لسه جديد أو ملوش wonum حقيقي، اخرج وما تفتحش المنيو
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
      // 1. التحقق لازم يكون جوه الدالة عشان يشتغل عند النداء فقط
      if (data.status !== 'INPRG') {
        return toast.error(t('wo.messages.actionNotAllowed', { status: data.status }), {
          icon: '❌',
          style: { borderRadius: '4px', background: '#333', color: '#fff', fontSize: '12px' }
        });
      }
      
      // 2. لو الحالة صح، شغّل نفس فحوصات "Change Status" (تكليفات مفتوحة + تجاوز ميزانية) قبل التنفيذ
      runPreStatusChecks(() => handleWOStatusAction('COMP', t('wo.actions.complete')));
    };

    window.addEventListener('GLOBAL_COMP_WO', triggerCompW);
    return () => window.removeEventListener('GLOBAL_COMP_WO', triggerCompW);
  }, [handleWOStatusAction, data.status]); 
  
  useEffect(() => {
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const triggerDelete = () => handleDelete();
    window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
  }, [handleDelete]);

  useEffect(() => {
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const triggerSLA = () => handleApplySLA();
    window.addEventListener('GLOBAL_SLA_HANDLER', triggerSLA);
    return () => window.removeEventListener('GLOBAL_SLA_HANDLER', triggerSLA);
  }, [handleApplySLA]);


  useEffect(() => {
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const openStatusModal = () => {
        if (isReadOnly && data.status === 'CAN') return Swal.fire({...swalTheme(), title: t('wo.messages.locked'), text: t('wo.messages.cantChangeCancel'), icon: 'lock'});
        setShowStatusModal(true);
    };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [isReadOnly]);

  useEffect(() => {
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const handleOpenHistory = () => {
      setShowHistoryModal(true);
    };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => {
      window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    };
  }, []);

  useEffect(() => {
    if (isNew || !data.wonum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const handleOpenWFHistory = () => {
      setIsWFHistoryOpen(true);
    };
    window.addEventListener('OPEN_WF_HISTORY', handleOpenWFHistory);
    return () => {
      window.removeEventListener('OPEN_WF_HISTORY', handleOpenWFHistory);
    };
    }, [isNew, data.wonum]);  
  
    useEffect(() => {
    fetchCount();
  }, [data.wonum, fetchCount]);

  useEffect(() => {
      loadNavigation();
  }, [loadNavigation]);    
  const goTo = (targetWonum) => {
    if (targetWonum) {
      navigate(`/workorders/${encodeURIComponent(targetWonum)}${location.search}`);
    }
  };

  const handlePrintSafetySheet = async () => {
    if (isNew ) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/print-safety-sheet/${data.wonum}`,
        data,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (error) {
      console.error("Print Safety Sheet Error:", error);
      toast.error(t('wo.messages.printFail'));
    }
  };

  const handlePrint = async () => {
    if (isNew ) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/print-wo/${data.wonum}`,
        data,
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
      );
  
      // تحويل الـ Blob لرابط وفتحه في صفحة جديدة
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } catch (error) {
      console.error("Print Error:", error);
      toast.error(t('wo.messages.printFail'));
    }
  };

  const handleRouteWorkflow = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/workflow/initiate', {
        module: 'WORKORDER',
        ref_id: id // رقم الـ WO
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      toast.success(t('wo.messages.wfRouted'));
      loadWO(); // عشان الزرار يختفي للي بدأه ويظهر للي عليه الدور
      fetchWFStatus();
    } catch (err) {
      toast.error(t('wo.messages.wfFail'));
    }
  };

  const fetchWFStatus = async () => {
    try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/api/workflow/check-runtime/WORKORDER/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setWfContext(res.data);
    } catch (err) {
        console.error("WF Check Failed", err);
    }
};

useEffect(() => {
    if (!isNew) fetchWFStatus();
}, [id, data.status]);

  return (
    <div className={`flex flex-col h-full bg-surface-app text-[0.8] overflow-hidden font-bold duration-300 transition-[font-size] ${isRTL ? 'font-cairo' : 'font-sans'}`}  style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({ ...lookup, isOpen: false })} 
        onSelect={handleLookupSelect} 
      />
      {isWFHistoryOpen && (
        <WorkflowHistoryModal 
          module="WORKORDER" 
          refId={id} 
          onClose={() => setIsWFHistoryOpen(false)} 
        />
      )}
      {showStatusModal && (
        <StatusModal 
          currentStatus={data.status}
          recordId={data.wonum}
          appName="WO"
          domainId={data.status === 'APPR' ? 'CANCEL_ONLY' : fieldMaps['status']}
          onClose={() => setShowStatusModal(false)}
          onComplete={async () => {
                await loadData(); 
                
                const event = new CustomEvent('SHOW_CREATE_PO_MENU', { 
                    detail: { prnum: data.wonum, status: 'APPR' } 
                });
                window.dispatchEvent(event);
            }}          
        />)}
        
      {showHistoryModal && (
      <StatusHistoryModal isOpen={showHistoryModal} appName="WO" recordId={data.wonum} onClose={() => setShowHistoryModal(false)} />)}
      {!isNew && (
      <AttachmentsModal 
          isOpen={isDocModalOpen} 
          onClose={() => {setIsDocModalOpen(false);fetchCount();}} 
          ownertable="WO" 
          ownerid={data.wonum} 
      />)}
      {hoveredAsset && (
        <div 
          className="fixed pointer-events-none transition-all duration-75"
          style={{ top: mousePos.y + 15, left: mousePos.x + 15, zIndex: 9999 }}
        >
          <AssetPreviewCard asset={hoveredAsset} />
        </div>
      )}
      {taskStatusConfig.isOpen && (
        <StatusModal 
          currentStatus={taskStatusConfig.currentStatus}
          recordId={taskStatusConfig.taskid} 
          appName="WOTASK" 
          domainId="WOSTATUS" 
          onClose={() => setTaskStatusConfig({ ...taskStatusConfig, isOpen: false })}
          onComplete={async (newStatus) => {
            const newTasks = [...data.tasks];
            newTasks[taskStatusConfig.index].status = newStatus;
            setData({ ...data, tasks: newTasks });
            setTaskStatusConfig({ ...taskStatusConfig, isOpen: false });
            toast.success(`Task ${taskStatusConfig.taskid} status changed to ${newStatus}`);
          }}
        />
      )}
      {showWOCostsModal && (
        <WOCostsModal 
          onClose={() => setShowWOCostsModal(false)} 
          wonum={data.wonum} 
        />
      )}

      {showPurchaseInfoModal && (
        <PurchaseInfoModal 
          onClose={() => setShowPurchaseInfoModal(false)} 
          wonum={data.wonum} 
        />
      )}

      {showIncidentModal && (
        <IncidentModal 
          isOpen={true} // لو المودال القديم متبرمج يقبل isOpen
          onClose={() => setShowIncidentModal(false)} 
          initialData={{
            assetnum: data.assetnum,
            site_id: data.site_id,
            org_id: data.org_id,
            wonum: data.wonum
          }}
        />
      )}
      {/* 🎯 استدعاء موديل طلب الخدمة (SR) */}
      {showSRModal && (
        <SRModal 
          onClose={() => setShowSRModal(false)} 
          initialData={{
            assetnum: data.assetnum,
            site_id: data.site_id,
            org_id: data.org_id,
            wonum: data.wonum
          }}
        />
      )}

      <AuditHistoryModal 
          isOpen={showAudit} 
          onClose={() => setShowAudit(false)} 
          tableName="workorders" // 🎯 من الـ Wizard Domain
          recordId={data.wonum} 
      />

{/* 1. TOP TOOLBAR */}
    <div className="bg-primary-dark text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4">
      <div className="flex items-center gap-3">
        {/* 🎯 لو الوصول هنا كان عن طريق رابط بيحمل "from" (زي تابة Work History
            في شاشة الأصول)، الرجوع بيروح لنفس المكان اللي جاي منه بدل قائمة
            أوامر الشغل العامة دايمًا */}
        <button onClick={() => navigate(location.state?.from || '/workorders')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-white/70">
          <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''}/>
        </button>

        {!isNew && (
          <div className={`flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1">
              <span className="text-status-warning">{nav.current_pos}</span> / {nav.total}
            </div>
            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-status-warning disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {isEditable && (
        <button onClick={handleSave} className="bg-status-success hover:bg-status-success text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] transition-all shadow-md uppercase">
          <Save size={14} /> {t('common.save')}
        </button>
        )}
        {wfContext.can_start && !['COMP', 'CANC', 'CLOSED', 'CLOSE', 'CAN'].includes(data.status) && (
            <button onClick={handleRouteWorkflow} className="bg-status-warning hover:bg-status-warning text-white border-0 flex items-center gap-2 text-[11px] font-black h-8 shadow-md uppercase">
                <Zap size={14} fill="currentColor" /> {t('wo.actions.routeWorkflow')}
            </button>
          )}

        <WorkflowActionModal moduleName="WORKORDER" refId={id} currentStatus={data.status} onActionComplete={loadWO} />
        <button onClick={() => setIsWFHistoryOpen(true)} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('wo.actions.wfHistory')}><Workflow size={16} className="text-status-warning"/></button>
        <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
        {!isNew && <SmartPrintButton moduleName="WORKORDER" refId={`${data.wonum}`} extraParams={{ location: data.location, report_type: 'GL_SUMMARY' }} />}
        {!isNew && !['COMP', 'CLOSE', 'CAN'].includes(data.status) && (
        <button onClick={handleStatusChangeInitiation} disabled={isReadOnly} className="p-2 hover:bg-white/10 rounded text-white/70 transition-colors" title={t('common.change_status')}>
          <Tag size={16}/>
        </button>
        )}
        {!isNew && (
        <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-white/70" title={t('wo.actions.attachments')}>
          <Paperclip size={16} />
          {count > 0 && <span className="absolute top-1 right-1 bg-status-danger text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
        </button>
        )}
        {!isNew && (
        <button onClick={handlePrint} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('wo.actions.printWO')}>
          <Printer size={16} />
        </button>
        )}
        {data.wohazards?.length > 0 && (
          <button onClick={handlePrintSafetySheet} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('wo.actions.printSafety')}>
            <Printer size={16} />
          </button>
        )}
        {!isNew && (
        <button onClick={() => setShowHistoryModal(true)} className="p-2 hover:bg-white/10 rounded text-white/70" title={t('wo.actions.history')}>
          <History size={16}/>
        </button>
        )}
        {!isNew && canDelete && (
          <button onClick={handleDelete} className="p-2 text-status-danger hover:bg-status-danger/20 rounded transition-colors" title={t('common.delete')}>
            <Trash2 size={16}/>
          </button>
        )}
        <FontControls />
      </div>
    </div>

    {/* 2. DOCUMENT HEADER  */}
    <div className="bg-surface-card border-b border-line px-6 py-3 flex items-center gap-12 shadow-sm animate-in fade-in slide-in-from-top duration-500">
      <div className="flex flex-col min-w-[120px]">
        <label className="text-[0.8] text-ink-secondary font-black uppercase tracking-widest">{t('wo.headers.workOrder')}</label>
        <span className="text-sm font-black text-primary-dark">{data.wonum || t('common.new')}</span>
      </div>

      <div className={`flex flex-col flex-1 border-line px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
        <label className="text-[0.8] text-ink-secondary font-black uppercase tracking-widest">{t('wo.fields.description')}</label>
        <input
          className={`text-sm font-bold text-ink outline-none focus:text-primary bg-transparent w-full ${isRTL ? 'text-right' : ''}`}
          value={data.description}
          onChange={e => setData({...data, description: e.target.value})}
          placeholder={t('wo.placeholders.desc')}
        />
      </div>

      {data.slanum && (
          <div className="bg-status-warning-bg border-l-4 border-status-warning p-4 mb-6 flex justify-between items-center shadow-md animate-pulse">
              <div className="flex items-center gap-3">
                  <Clock className="text-status-warning" size={24}/>
                  <div>
                      <p className="text-[0.7] font-black text-status-warning">{t('wo.headers.activeSla')}</p>
                      <p className="text-primary font-black">{data.slanum} - {data.sladescription}</p>
                  </div>
              </div>
              <div className="text-right">
                  <p className="text-[0.6] text-ink-secondary font-black">{data.targcompdate ? t('wo.headers.targetRes') : t('wo.headers.targetResp')}</p>
                  <p className="text-status-danger font-black text-sm italic">
                      {new Date(data.targcompdate || data.targstartdate).toLocaleString()}
                  </p>
              </div>
          </div>
      )}
      <div className={`flex flex-col min-w-[100px] border-line px-8 ${isRTL ? 'border-r' : 'border-l'}`}>
        <label className="text-[0.8] text-ink-secondary font-black uppercase tracking-widest">{t('wo.headers.status')}</label>
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full w-fit mt-1
          ${data.status === 'WAPPR' ? 'bg-status-warning-bg text-status-warning' : 'bg-status-success-bg text-status-success'}`}>
          {data.status}
        </span>
      </div>

      <div className={`flex flex-col min-w-[80px] border-line px-8 ${isRTL ? 'border-r text-left' : 'border-l text-right'}`}>
        <label className="text-[0.8] text-ink-secondary font-black uppercase tracking-widest">{t('wo.headers.totalCost')}</label>
        <span className="text-sm font-black text-status-success">{getActualTotal().toLocaleString()} <small className="text-[0.7]">{currency}</small></span>
      </div>
    </div>

    {/* 3. TABS NAVIGATION (التابات بقت أهدى) */}
    <div className="flex bg-surface-card border-b border-line-subtle px-4 overflow-x-auto no-scrollbar">
      {[
        { id: 'Work Order', label: t('wo.tabs.workOrder'), icon: <Briefcase size={13}/> },
        { id: 'Plans', label: t('wo.tabs.plans'), icon: <FileText size={13}/> },
        { id: 'Assignments', label: t('wo.tabs.assignments'), icon: <UserCheck size={13}/> },
        { id: 'Actuals', label: t('wo.tabs.actuals'), icon: <Activity size={13}/> },
        { id: 'Safety', label: t('wo.tabs.safety'), icon: <ShieldAlert size={13}/> },
        { id: 'Failure Reporting', label: t('wo.tabs.failure'), icon: <AlertTriangle size={13}/> }
      ].map(tItem => (
        <button key={tItem.id} onClick={() => setActiveTab(tItem.id)} 
          className={`px-6 py-2.5 flex items-center gap-2 transition-all whitespace-nowrap text-[10px] font-black uppercase tracking-tighter
          ${activeTab === tItem.id ? 'border-b-2 border-primary-dark text-primary-dark' : 'text-ink-secondary hover:text-ink'}`}>
          {tItem.icon} {tItem.label}
        </button>
      ))}
    </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-surface-panel">
        
        {activeTab === 'Work Order' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <Section title={t('wo.sections.assetDetails')}>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                  <DataField label={t('wo.fields.location')} value={data.location_id} desc={data.location_description} onSearch={() => handleSearch('location_id', t('wo.actions.selectLoc'),'locations')} isRTL={isRTL} />                
                  <DataField label={t('wo.fields.asset')} value={data.assetnum} desc={data.asset_description} onSearch={() => handleSearch('assetnum', t('wo.actions.selectAsset'),'assets')} onMouseEnter={(e) => handleMouseEnter(e, data.assetnum)} onMouseLeave={() => setHoveredAsset(null)} isRTL={isRTL}/>
                  <DataField label={t('wo.fields.workType')} value={data.wo_type} onSearch={() => handleSmartSearch('wo_type', t('wo.actions.selectType'),'DOMAIN')} isRTL={isRTL} />
                  <DataField label={t('wo.fields.safetyCode')} value={data.safetypnum} onSearch={() => handleSmartSearch('safetypnum', t('wo.actions.selectSafety'),'safetyplans')} isRTL={isRTL} />
                  <DataField label={t('wo.fields.supervisor')} value={data.supervisor} onSearch={() => handleSearch('supervisor',t('wo.actions.selectSup'), 'labors')} isRTL={isRTL} />
                  <DataField label={t('wo.fields.failureClass')} value={data.failure_class} onSearch={() => handleSearch('failure_class',t('wo.actions.selectFail'), 'failure-classes')} isRTL={isRTL} />
                  <CustomFieldSlot coreModelName="WORKORDER" tab="Work Order" section="Asset Details" recordId={data.wonum} disabled={isNew} />
                </div>
                {/* 🎯 كارت تحديد مكان العطل على الأصل الخطي (مترجم بالكامل) */}
                {data.is_linear && (
                  <div className="col-span-2 mt-4 p-4 border border-status-warning-bg bg-status-warning-bg/30 rounded-lg animate-in fade-in duration-300">
                    <h4 className="text-[10px] font-black uppercase text-status-warning tracking-widest mb-3 flex items-center gap-2">
                      <Activity size={14} /> {t('wo.linearDefect.title')}
                    </h4>
                    <div className="grid grid-cols-4 gap-6">
                      <EditableField 
                        label={t('wo.linearDefect.startMeasure')} 
                        type="number" 
                        value={data.start_measure} 
                        onChange={v => setData({...data, start_measure: v})} 
                      />
                      <EditableField 
                        label={t('wo.linearDefect.endMeasure')} 
                        type="number" 
                        value={data.end_measure} 
                        onChange={v => setData({...data, end_measure: v})} 
                      />
                      <EditableField 
                        label={t('wo.linearDefect.unit')} 
                        value={data.measure_unit} 
                        readOnly 
                      />
                      <EditableField 
                        label={t('wo.linearDefect.yOffset')} 
                        type="number" 
                        value={data.y_offset} 
                        onChange={v => setData({...data, y_offset: v})} 
                      />
                    </div>
                  </div>
                )}
              </Section>
              {/* --- Scheduling Information  --- */}
              <Section title={t('wo.sections.scheduling')}>
                <div className="grid grid-cols-2 gap-x-20 gap-y-6 p-2">
                  
                  {/*  Target & Scheduled */}
                  <div className="space-y-4">
                    <DateField label={t('wo.fields.targStart')} value={data.targstartdate} readOnly={data.slanum ? true : false} onChange={v => setData({...data, targstartdate: v})} hasCalendar isRTL={isRTL} />
                    <DateField label={t('wo.fields.targFinish')} value={data.targcompdate} onChange={v => setData({...data, targcompdate: v})} hasCalendar isRTL={isRTL} />
                    <DateField label={t('wo.fields.schedStart')} value={data.scheddate} onChange={v => setData({...data, scheddate: v})} hasCalendar isRTL={isRTL} />
                    <DateField label={t('wo.fields.schedFinish')} value={data.schedfinish} onChange={v => setData({...data, schedfinish: v})} hasCalendar isRTL={isRTL} />
                    <DateField label={t('wo.fields.earlyStart')} value={data.earlystart} onChange={v => setData({...data, earlystart: v})} hasCalendar isRTL={isRTL} />
                    <DateField label={t('wo.fields.lateFinish')} value={data.latefinish} onChange={v => setData({...data, latefinish: v})} hasCalendar isRTL={isRTL} />
                  </div>
        
                  {/*  Actuals & Duration */}
                  <div className="space-y-4">
                    <DateField label={t('wo.fields.actStart')} value={data.actstart} onChange={v => setData({...data, actstart: v})} hasCalendar isRTL={isRTL} />
                    <DateField label={t('wo.fields.actFinish')} value={data.actfinish} onChange={v => setData({...data, actfinish: v})} hasCalendar isRTL={isRTL} />
                    
                    <div className="flex items-center gap-4 border-b border-line pb-1">
                       <div className="flex-1">
                          <EditableField label={`* ${t('wo.fields.duration')}`} value={data.duration} type="text" placeholder="0:00" onChange={v => setData({...data, estdur: v})} isHighlight isRTL={isRTL} />
                       </div>
                       <div className="text-[9px] text-ink font-bold self-end pb-1">{t('hrs')}</div>
                    </div>
        
                    <EditableField label={t('wo.fields.timeRemaining')} value={data.timeremaining} readOnly />
                    <DataField label={t('wo.fields.predecessors')} value={data.predecessors} onSearch={() => handleSmartSearch('predecessors', 'Predecessors', 'wo')} />
                    
                    <div className="flex items-center gap-2 pt-2">
                      <input 
                        type="checkbox" 
                        checked={data.include_tasks_in_sched || false} 
                        onChange={(e) => setData({...data, include_tasks_in_sched: e.target.checked})} 
                        className="w-4 h-4 rounded accent-blue-600 cursor-pointer" 
                      />
                      <span className="font-bold text-primary uppercase text-[10px]">{t('wo.fields.includeTasks')}</span>
                    </div>
                  </div>

                </div>
                <div className="grid grid-cols-2 gap-x-12 gap-y-4 mt-4">
                  <CustomFieldSlot coreModelName="WORKORDER" tab="Work Order" section="Scheduling Information" recordId={data.wonum} disabled={isNew} />
                </div>
              </Section>

            </div>
            <div className="col-span-4">
              <Section title={t('wo.sections.responsibility')}>
                <div className="space-y-4">
                  <DataField label={t('wo.fields.site')} value={data.site_id} />
                  <DataField label={t('wo.fields.organization')} value={data.org_id} />
                  <div className="p-4 bg-status-warning-bg border border-status-warning-bg rounded-lg text-status-warning font-bold uppercase">
                     <EditableField label={t('wo.fields.priorityHigh')} value={data.priority} />
                  </div>
                  <CustomFieldSlot coreModelName="WORKORDER" tab="Work Order" section="Responsibility" recordId={data.wonum} disabled={isNew} />
                </div>
              </Section>
            </div>
            <div className="col-span-12 grid grid-cols-2 gap-x-12 gap-y-4">
              <CustomFieldSlot coreModelName="WORKORDER" tab="Work Order" section={null} recordId={data.wonum} disabled={isNew} />
            </div>
          </div>
        )}

        {activeTab === 'Plans' && (
          <div className="max-w-7xl mx-auto bg-surface-card rounded shadow-xl overflow-hidden animate-in slide-in-from-bottom duration-300 min-h-[500px] flex flex-col">
            {/* Sub-Tabs Header */}
            <div className="flex bg-surface-panel border-b shadow-inner">
              {['Tasks', 'Labor', 'Materials', 'Tools'].map(st => (
                <button key={st} onClick={() => setPlanSubTab(st)}
                  className={`px-10 py-3 text-[0.9] font-black uppercase transition-all
                  ${planSubTab === st ? 'bg-surface-card border-t-4 border-t-primary text-primary' : 'text-ink hover:text-primary'}`}>
                  {t(`wo.subTabs.${st.toLowerCase()}`)}
                </button>
              ))}
            </div>

            {/* Sub-Tabs Content */}
            <div className="p-4 flex-1 bg-surface-card overflow-y-auto custom-scrollbar">

              {/* 1. Tasks Section */}
              {planSubTab === 'Tasks' && (
                <div className="animate-in fade-in duration-300">
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel text-ink text-[0.8e] uppercase border-b font-black">
                      <tr>
                        <th className="p-2 border-r w-20 text-center">{t('wo.tables.sequence')}</th>
                        <th className="p-2 border-r text-left">{t('wo.tables.taskDesc')}</th>
                        <th className="p-2 border-r w-24 text-center">{t('wo.tables.estDur')}</th>
                        <th className="p-2 border-r w-24 text-center">{t('wo.tables.status')}</th>
                        <th className="p-2 w-10 text-center text-status-danger"><Tag size={12}/></th>
                        <th className="p-2 w-10 text-center text-status-danger"><Trash2 size={12}/></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pagedRows('tasks', data.tasks).map(({ row, idx }) => (
                        <tr key={idx} className="hover:bg-primary-soft transition-colors">
                          <td className="p-2 border-r text-center font-bold text-primary">{row.wosequence}</td>
                          <td className="p-2 border-r">
                            <input className="w-full outline-none bg-transparent font-semibold uppercase" value={row.description} 
                                   onChange={e => updateRow('tasks', idx, 'description', e.target.value)} />
                          </td>
                          <td className="p-2 border-r">
                            <input type="number" className="w-full outline-none bg-transparent text-center font-mono" value={row.estdur} 
                                   onChange={e => updateRow('tasks', idx, 'estdur', e.target.value)} />
                          </td>
                          <td className="p-2 border-r">
                            <input className="w-full outline-none bg-transparent text-center font-mono" value={row.status} 
                                   onChange={e => updateRow('tasks', idx, 'status', e.target.value)} disabled />
                          </td>
                          <td className="p-2 text-center">
                            <button onClick={(e) => {e.stopPropagation(); handleTaskStatusClick(row, idx)}} className="text-ink-secondary hover:text-status-danger"><Tag size={14}/></button>
                          </td>
                          <td className="p-2 text-center">
                            <button onClick={() => removeRow('tasks', idx)} className="text-ink-secondary hover:text-status-danger"><Trash2 size={14}/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rowPager('tasks', data.tasks)}
                  <button onClick={addTaskRow} className="mt-4 bg-primary text-white px-6 py-2 rounded-sm flex items-center gap-2 text-[9px] font-black hover:bg-black uppercase shadow-md transition-all">
                    <Plus size={14}/> {t('wo.actions.newTask')}
                  </button>
                </div>
              )}

              {/* 2. Labor Section */}
              {planSubTab === 'Labor' && (
                <div className="animate-in fade-in duration-300">
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel text-ink text-[0.8] uppercase border-b font-black">
                      <tr>
                        <th className="p-2 border-r text-left w-32">{t('wo.tables.craft')}</th>
                        <th className="p-2 border-r text-left w-32">{t('wo.tables.labor')}</th>
                        <th className="p-2 border-r w-16 text-center">{t('wo.tables.qty')}</th>
                        <th className="p-2 border-r w-16 text-center">{t('wo.tables.hrs')}</th>
                        <th className="p-2 border-r w-24 text-center">{t('wo.tables.rate')}</th>
                        <th className="p-2 border-r w-28 text-center bg-primary-soft">{t('wo.tables.lineCost')}</th>
                        <th className="p-2 w-10 text-center text-status-danger"><Trash2 size={12}/></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pagedRows('planned_labor', data.planned_labor).map(({ row, idx }) => (
                        <tr key={idx} className="hover:bg-primary-soft transition-colors">
                          <td className="p-2 border-r">
                            <div className="flex justify-between items-center group">
                              <span className="font-black text-primary uppercase">{row.craft || 'SELECT'}</span>
                              <Search size={12} className="text-primary cursor-pointer" onClick={() => handleSearch('row_plan_craft', 'Select Craft', 'crafts', idx)} onChange={e => updateRow('planned_labor', idx, 'craft', e.target.value)} />
                            </div>
                          </td>
                          <td className="p-2 border-r">
                            <div className="flex justify-between items-center group">
                              <div className="flex flex-col">
                                <span className="font-black text-primary uppercase">{row.laborcode || 'SELECT'}</span>
                                {/* 👈 ده السطر اللي هيعرض اسم العامل */}
                                {row.laborname && <span className="text-[0.7em] text-ink-secondary font-bold italic truncate max-w-[120px]">{row.laborname}</span>}
                              </div>
                              <Search size={12} className="text-primary cursor-pointer" onClick={() => handleSearch('row_plan_labor', 'Select Labor', 'labors', idx)} onChange={e => updateRow('planned_labor', idx, 'laborcode')} />
                            </div>
                          </td>
                          <td className="p-2 border-r"><input type="number" className="w-full text-center outline-none bg-transparent" value={row.quantity} onChange={e => updateRow('planned_labor', idx, 'quantity', e.target.value)} /></td>
                          <td className="p-2 border-r"><input type="number" className="w-full text-center outline-none bg-transparent" value={row.laborhrs} onChange={e => updateRow('planned_labor', idx, 'laborhrs', e.target.value)} /></td>
                          <td className="p-2 border-r text-center text-ink font-mono italic">{row.laborrate}</td>
                          <td className="p-2 border-r text-center font-black text-status-success bg-primary-soft">{Number(row.linecost).toLocaleString()}</td>
                          <td className="p-2 text-center">
                            <button onClick={() => removeRow('planned_labor', idx)} className="text-ink-secondary hover:text-status-danger"><Trash2 size={14}/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rowPager('planned_labor', data.planned_labor)}
                  <button onClick={addLaborRow} className="mt-4 bg-primary text-white px-6 py-2 rounded-sm flex items-center gap-2 text-[9px] font-black hover:bg-black uppercase shadow-md transition-all">
                    <Plus size={14}/> {t('wo.actions.addLabor')}
                  </button>
                </div>
              )}
        
              {/* 3. Materials Section */}
              {planSubTab === 'Materials' && (
                <div className="animate-in fade-in duration-300">
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel text-ink text-[0.8] uppercase border-b font-black">
                      <tr>
                        <th className="p-2 border-r text-left w-24">{t('wo.tables.lineType')}</th>
                        <th className="p-2 border-r text-left w-32">{t('wo.tables.itemNum')}</th>
                        <th className="p-2 border-r text-left">{t('wo.tables.description')}</th>
                        <th className="p-2 border-r w-20 text-center">{t('wo.tables.qty')}</th>
                        <th className="p-2 border-r w-24 text-center">{t('wo.tables.unitCost')}</th>
                        <th className="p-2 border-r w-28 text-center bg-primary-soft text-primary">{t('wo.tables.lineCost')}</th>
                        <th className="p-2 border-r text-left w-32 text-status-warning">{t('wo.tables.storeroom')}</th>
                        <th className="p-2 w-10 text-center text-status-danger"><Trash2 size={12}/></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pagedRows('planned_materials', data.planned_materials).map(({ row, idx }) => (
                        <tr key={idx} className="hover:bg-primary-soft transition-colors">
                          <td className="p-2 border-r">
                            <select className="w-full bg-transparent outline-none font-bold text-primary uppercase" value={row.linetype} onChange={e => updateMaterialRow(idx, 'linetype', e.target.value)}>
                              <option value="ITEM">{t('wo.tables.item')}</option>
                              <option value="MATERIAL">{t('wo.tables.material')}</option>
                            </select>
                          </td>
                          <td className="p-2 border-r font-black text-primary">
                            <div className="flex justify-between items-center group">
                              <span>{row.itemnum || '---'}</span>
                              <Search size={12} className="text-primary cursor-pointer" onClick={() => handleSearch('row_plan_item', 'Select Item', 'items', idx)} />
                            </div>
                          </td>
                          <td className="p-2 border-r text-ink-secondary italic font-medium">{row.description}</td>
                          <td className="p-2 border-r"><input type="number" className="w-full text-center outline-none bg-transparent" value={row.quantity} onChange={e => updateMaterialRow(idx, 'quantity', e.target.value)} /></td>
                          <td className="p-2 border-r"><input type="number" className="w-full text-center outline-none bg-transparent font-mono" value={row.unitcost} onChange={e => updateMaterialRow(idx, 'unitcost', e.target.value)} /></td>
                          <td className="p-2 border-r text-center font-black text-status-success bg-primary-soft">{Number(row.linecost).toLocaleString()}</td>
                          <td className="p-2 border-r">
                            <div className="flex justify-between items-center group">
                              <span className="text-status-warning font-bold uppercase">{row.fromstore || '---'}</span>
                              <Search size={12} className="text-status-warning cursor-pointer" onClick={() => handleSearch('row_plan_store', 'Select Store', 'storerooms', idx)} />
                            </div>
                          </td>
                          <td className="p-2 text-center">
                            <button onClick={() => removeRow('planned_materials', idx)} className="text-ink-secondary hover:text-status-danger"><Trash2 size={14}/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rowPager('planned_materials', data.planned_materials)}
                  <button onClick={addMaterialRow} className="mt-4 bg-primary text-white px-6 py-2 rounded-sm flex items-center gap-2 text-[9px] font-black hover:bg-black uppercase shadow-md transition-all">
                    <Plus size={14}/> {t('wo.actions.addMaterial')}
                  </button>
                </div>
              )}

              {/* 4. Tools Section */}
              {planSubTab === 'Tools' && (
                <div className="animate-in fade-in duration-300">
                  <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel text-ink text-[0.8] uppercase border-b font-black">
                      <tr>
                        <th className="p-2 border-r text-left w-32">{t('wo.tables.toolNum')}</th>
                        <th className="p-2 border-r text-left">{t('wo.tables.description')}</th>
                        <th className="p-2 border-r w-20 text-center">{t('wo.tables.hrs')}</th>
                        <th className="p-2 border-r w-24 text-center">{t('wo.tables.rate')}</th>
                        <th className="p-2 border-r w-28 text-center bg-primary-soft">{t('wo.tables.lineCost')}</th>
                        <th className="p-2 w-10 text-center text-status-danger"><Trash2 size={12}/></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pagedRows('planned_tools', data.planned_tools).map(({ row, idx }) => (
                        <tr key={idx} className="hover:bg-primary-soft transition-colors">
                          <td className="p-2 border-r font-black text-primary uppercase">
                            <div className="flex justify-between items-center group">
                              <span>{row.toolnum || '---'}</span>
                              <Search size={12} className="text-primary cursor-pointer" onClick={() => handleSearch('row_plan_tool', 'Select Tool', 'tools', idx)} />
                            </div>
                          </td>
                          <td className="p-2 border-r text-ink-secondary font-semibold italic">{row.description}</td>
                          <td className="p-2 border-r"><input type="number" className="w-full text-center outline-none bg-transparent" value={row.hours} onChange={e => updateRow('planned_tools', idx, 'hours', e.target.value)} /></td>
                          <td className="p-2 border-r"><input type="number" className="w-full text-center outline-none bg-transparent font-mono text-ink" value={row.toolrate} onChange={e => updateRow('planned_tools', idx, 'toolrate', e.target.value)} /></td>
                          <td className="p-2 border-r text-center font-black text-status-success bg-primary-soft">{Number(row.linecost).toLocaleString()}</td>
                          <td className="p-2 text-center">
                            <button onClick={() => removeRow('planned_tools', idx)} className="text-ink-secondary hover:text-status-danger"><Trash2 size={14}/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {rowPager('planned_tools', data.planned_tools)}
                  <button onClick={addToolRow} className="mt-4 bg-primary text-white px-6 py-2 rounded-sm flex items-center gap-2 text-[9px] font-black hover:bg-black uppercase shadow-md transition-all">
                    <Plus size={14}/> {t('wo.actions.addTool')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === 'Assignments' && (
          <div className="max-w-7xl mx-auto bg-surface-card rounded shadow-xl overflow-hidden animate-in slide-in-from-bottom duration-300 min-h-[500px] flex flex-col">
            <div className="p-4 bg-surface-panel border-b flex justify-between items-center">
              <h3 className="text-primary font-black uppercase text-[0.9] tracking-widest flex items-center gap-2">
                <UserCheck size={16}/> {t('wo.sections.laborAssign')}
              </h3>
              <span className="text-[0.9] bg-primary-soft text-primary px-2 py-0.5 rounded-full font-bold">
                {t('wo.sections.totalAssign')} : {data.assignments.length}
              </span>
            </div>

            <div className="p-4 flex-1 overflow-x-auto">
              <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                <thead className="bg-surface-panel text-ink text-[0.8] uppercase border-b font-black">
                  <tr>
                    <th className={`p-2 border-x w-32 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('wo.tables.craft')}</th>
                    <th className={`p-2 border-x w-40 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('wo.tables.labor')}</th>
                    <th className={`p-2 border-x ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>{t('wo.tables.description')}</th>
                    <th className={`p-2 border-x w-16 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.qty')}</th>
                    <th className={`p-2 border-x w-16 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.hrs')}</th>
                    <th className={`p-2 border-x w-20 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.rate')}</th>
                    <th className={`p-2 border-x w-24 text-center bg-primary-soft ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.lineCost')}</th>
                    <th className={`p-2 border-x w-24 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('wo.tables.status')}</th>
                    <th className="p-2 w-10 text-center text-status-danger"><CheckCircle size={12}/></th>
                    <th className="p-2 w-10 text-center text-status-danger"><Trash2 size={12}/></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pagedRows('assignments', data.assignments).map(({ row, idx }) => (
                    <tr key={idx} className="hover:bg-primary-soft transition-colors text-[10px]">
                      {/* Craft Selection */}
                      <td className="p-2 border-r">
                        <div className="flex justify-between items-center group">
                          <span className="font-black text-primary uppercase">{row.craft || 'SELECT'}</span>
                          <Search size={12} className="text-primary cursor-pointer"
                            onClick={() => handleSearch('row_assign_craft', 'Select Craft', 'crafts', idx)} />
                        </div>
                      </td>
                      {/* Labor Selection */}
                      <td className="p-2 border-r">
                        <div className="flex justify-between items-center group">
                          <span className="font-black text-primary uppercase">{row.laborcode || 'SELECT'}</span>
                          {row.laborname && <span className="text-[0.7em] text-ink-secondary font-bold italic truncate max-w-[120px]">{row.laborname}</span>}
                          <Search size={12} className="text-primary cursor-pointer"
                            onClick={() => handleSearch('row_assign_labor', 'Select Labor', 'labors', idx)} />
                        </div>
                      </td>
                      {/* Description */}
                      <td className="p-2 border-r">
                        <input className="w-full outline-none bg-transparent font-semibold uppercase"
                          value={row.description} placeholder="TASK DESCRIPTION..."
                          onChange={e => updateAssignmentRow(idx, 'description', e.target.value)} />
                      </td>
                      {/* Quantity & Hours */}
                      <td className="p-2 border-r">
                        <input type="number" className="w-full text-center outline-none bg-transparent"
                          value={row.quantity} onChange={e => updateAssignmentRow(idx, 'quantity', e.target.value)} />
                      </td>
                      <td className="p-2 border-r">
                        <input type="number" className="w-full text-center outline-none bg-transparent font-mono"
                          value={row.laborhrs} onChange={e => updateAssignmentRow(idx, 'laborhrs', e.target.value)} />
                      </td>
                      {/* Rate & Line Cost */}
                      <td className="p-2 border-r text-center text-ink-secondary font-mono italic">{row.laborrate}</td>
                      <td className="p-2 border-r text-center font-black text-status-success bg-primary-soft">
                        {Number(row.linecost).toLocaleString()}
                      </td>
                      {/* Status */}
                      <td className="p-2 border-r text-center font-bold text-status-warning uppercase italic">
                        {row.status}
                      </td>
                      {/* Complet */}
                      <td className="p-2 text-center">
                        {row.status !== 'COMPLETED' && isEditable && (
                            <button
                              onClick={() => handleCompleteAssignment(idx)}
                              className="p-1 hover:bg-status-success-bg rounded-full transition-all group"
                              title="Complete Assignment"
                            >
                              <CheckCircle size={15} className="text-ink-secondary group-hover:text-status-success" />
                            </button>
                          )}
                      </td>
                      {/* Actions */}
                      <td className="p-2 text-center">
                        {isEditable && (
                          <button onClick={() => removeRow('assignments', idx)} className="text-ink-secondary hover:text-status-danger">
                            <Trash2 size={14}/>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rowPager('assignments', data.assignments)}

              {data.assignments.length === 0 && (
                <div className="flex flex-col items-center justify-center p-20 text-ink-secondary">
                  <UserCheck size={48} className="opacity-20 mb-2" />
                  <p className="italic">{t('wo.messages.noLaborAssign')}</p>
                </div>
              )}

              {isEditable && (
                <button onClick={addAssignmentRow} className="mt-4 bg-primary text-white px-6 py-2 rounded-sm flex items-center gap-2 text-[9px] font-black hover:bg-black uppercase shadow-md transition-all">
                  <Plus size={14}/> {t('wo.actions.newAssign')}
                </button>
              )}
            </div>
          </div>
        )}

            {/* 2. Actuals Content Area */}
            {activeTab === 'Actuals' && (
              <div className="max-w-7xl mx-auto bg-surface-card rounded shadow-2xl overflow-hidden animate-in slide-in-from-right duration-500 min-h-[650px] flex flex-col border border-line">
                
                {/* 1. Actuals Sub-Tabs Header */}
                <div className="flex bg-surface-panel border-b border-line sticky top-0 z-10">
                  {['Tasks', 'Labor', 'Materials', 'Tools', 'Services'].map((st) => (
                    <button
                      key={st}
                      onClick={() => setActualSubTab(st)}
                      className={`px-10 py-4 text-[0.8] font-black uppercase tracking-wider transition-all
                      ${actualSubTab === st
                        ? 'bg-surface-card text-status-success border-t-4 border-t-status-success shadow-sm'
                        : 'text-ink-secondary hover:text-status-success hover:bg-surface-panel'}`}
                    >
                      {t(`wo.subTabs.${st.toLowerCase()}`)}
                    </button>
                  ))}
                </div>

                {/* 2. Actuals Content Area */}
                <div className="p-6 flex-1 overflow-y-auto custom-scrollbar bg-surface-card">

                  {/* ���� عرض التاسكات اللي جاية من البلانز أوتوماتيك */}
                  {actualSubTab === 'Tasks' && (
                    <div className="animate-in fade-in duration-300">
                      <p className="text-[10px] text-ink mb-4 italic uppercase">{t('wo.messages.updateTaskProg')}</p>
                      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-surface-panel text-ink-secondary text-[0.8] uppercase font-black border-b">
                          <tr>
                            <th className="p-3 text-left w-20">{t('wo.tables.sequence')}</th>
                            <th className="p-3 text-left">{t('wo.tables.taskDesc')}</th>
                            <th className="p-3 text-center w-32">{t('wo.tables.status')}</th>
                            <th className="p-3 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRows('tasks', data.tasks).map(({ row, idx }) => (
                            <tr key={idx} className="border-b border-line-subtle hover:bg-status-success-bg">
                              <td className="p-3 font-bold text-ink-secondary">{row.wosequence}</td>
                              <td className="p-3 text-ink font-semibold">{row.description}</td>
                              <td className="p-3 text-center">
                                <button onClick={() => handleTaskStatusClick(row, idx)} className={`px-4 py-1 rounded-full text-[10px] font-black uppercase border-2 transition-all ${row.status === 'COMP' ? 'bg-status-success border-status-success text-white' : 'border-status-success text-status-success hover:bg-status-success hover:text-white'}`}>
                                  {row.status}
                                </button>
                              </td>
                              <td className="p-3 text-center"><Trash2 size={14} className="text-ink-secondary cursor-not-allowed" /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {rowPager('tasks', data.tasks)}
                    </div>
                  )}

                  {/* Actual Labor */}
                  {actualSubTab === 'Labor' && (
                    <div className="animate-in fade-in duration-300">
                      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-surface-panel text-ink-secondary text-[0.8] uppercase font-black border-b">
                          <tr>
                            <th className="p-2 text-left">{t('wo.tables.labor')}</th>
                            <th className="p-2 text-center w-44">{t('wo.tables.start')}</th>
                            <th className="p-2 text-center w-44">{t('wo.tables.finish')}</th>
                            <th className="p-2 text-center w-16">{t('wo.tables.hrs')}</th>
                            <th className="p-2 text-center w-24">{t('wo.tables.rate')}</th>
                            <th className="p-2 text-center w-32 bg-status-success-bg">{t('wo.tables.cost')}</th>
                            <th className="p-2 w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRows('actual_labor', data.actual_labor).map(({ row, idx }) => (
                            <tr key={idx} className="border-b hover:bg-status-success-bg text-[0.9]">
                              <td className="p-2 border-r font-bold text-primary flex justify-between items-center uppercase">
                                <span className="font-bold text-primary uppercase">{row.laborcode || '---'}</span>
                                {row.laborname && <span className="text-[0.7em] text-ink-secondary font-bold italic truncate max-w-[120px]">{row.laborname}</span>}
                                <Search size={12} className="cursor-pointer text-primary" onClick={() => handleSearch('row_act_labor', 'Labor', 'labors', idx)}/>
                              </td>
                              <td className="p-2 border-r text-center"><input type="datetime-local" className="bg-transparent outline-none" value={formatDateTime(row.startdate)} onChange={e => updateActualLaborRow(idx, 'startdate', e.target.value)} /></td>
                              <td className="p-2 border-r text-center"><input type="datetime-local" className="bg-transparent outline-none" value={formatDateTime(row.finishdate)} onChange={e => updateActualLaborRow(idx, 'finishdate', e.target.value)} /></td>
                              <td className="p-2 border-r text-center font-black text-primary">{row.regularhrs || 0}</td>
                              <td className="p-2 border-r text-center italic">{row.laborrate}</td>
                              <td className="p-2 border-r text-center font-black text-status-success">{Number(row.linecost || 0).toLocaleString()}</td>
                              <td className="p-2 text-center"><Trash2 size={12} className="text-status-danger cursor-pointer" onClick={() => removeRow('actual_labor', idx)}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {rowPager('actual_labor', data.actual_labor)}
                      <button onClick={() => addRow('actual_labor')} className="mt-4 bg-status-success text-white px-6 py-2 rounded-sm text-[9px] font-black uppercase shadow-md"><Plus size={14}/> {t('wo.actions.addLabor')}</button>
                    </div>
                  )}
            
                  {/* Actual Materials */}
                  {actualSubTab === 'Materials' && (
                    <div className="animate-in fade-in duration-300">
                      <div className="border rounded-sm overflow-hidden bg-surface-card">
                        <table className={`w-full table-fixed border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                          <thead className="bg-surface-panel text-ink-secondary text-[0.8] uppercase font-black border-b">
                            <tr>
                              <th className="p-2 border-r text-left w-[30%]">{t('wo.tables.itemNum')}</th>
                              <th className="p-2 border-r text-left w-[30%] text-status-warning">{t('wo.tables.storeroom')}</th>
                              <th className="p-2 border-r text-center w-[15%]">{t('wo.tables.qty')}</th>
                              <th className="p-2 border-r text-center w-[15%] bg-status-success-bg">{t('wo.tables.lineCost')}</th>
                              <th className="p-2 w-[10%]"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-line-subtle">
                            {pagedRows('actual_materials', data.actual_materials).map(({ row, idx }) => (
                              <tr key={idx} className="hover:bg-surface-panel transition-colors text-[10px]">
                                {/* Item Column */}
                                <td className="p-2 border-r">
                                  <div className="flex justify-between items-center bg-surface-panel/50 px-2 py-1 rounded-sm border border-transparent hover:border-primary transition-all">
                                    <span className="font-bold text-ink truncate">{row.itemnum || '---'}</span>
                                    <Search
                                      size={14}
                                      className="text-primary cursor-pointer hover:scale-110 shrink-0 ml-1"
                                      onClick={() => handleSearch('row_act_item', 'Select Item', 'items', idx)}
                                    />
                                  </div>
                                </td>

                                {/* Storeroom Column */}
                                <td className="p-2 border-r">
                                  <div className="flex justify-between items-center bg-status-warning-bg/30 px-2 py-1 rounded-sm border border-transparent hover:border-status-warning transition-all text-status-warning">
                                    <span className="font-black truncate">{row.fromstore || 'SELECT STORE'}</span>
                                    <Search
                                      size={14}
                                      className="text-status-warning cursor-pointer hover:scale-110 shrink-0 ml-1"
                                      onClick={() => handleSearch('row_act_store', 'Select Store', 'storerooms', idx)}
                                    />
                                  </div>
                                </td>

                                {/* Quantity Column */}
                                <td className="p-2 border-r text-center">
                                  <input
                                    type="number"
                                    className="w-full bg-transparent text-center font-black text-primary outline-none focus:ring-1 focus:ring-primary rounded-sm"
                                    value={row.quantity}
                                    onChange={e => updateActualMaterialRow(idx, 'quantity', e.target.value)}
                                  />
                                </td>

                                {/* Line Cost Column */}
                                <td className="p-2 border-r text-center bg-status-success-bg">
                                  <span className="font-black text-status-success">{Number(row.linecost || 0).toLocaleString()}</span>
                                </td>

                                {/* Action Column */}
                                <td className="p-2 text-center">
                                  <button
                                    onClick={() => handleCreateMR(row)}
                                    className="p-1 hover:bg-status-warning-bg rounded-full transition-colors group/mr"
                                    title="Create Material Request (MR)"
                                  >
                                    <FileText size={14} className="text-ink-secondary group-hover/mr:text-status-warning" />
                                  </button>
                                  <button
                                    onClick={() => removeRow('actual_materials', idx)}
                                    className="p-1 hover:bg-status-danger-bg rounded-full transition-colors group"
                                  >
                                    <Trash2 size={14} className="text-ink-secondary group-hover:text-status-danger" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {rowPager('actual_materials', data.actual_materials)}

                      {data.actual_materials.length === 0 && (
                        <div className="text-center p-12 border-2 border-dashed border-line-subtle rounded-sm mt-2">
                          <span className="text-ink-secondary italic text-[0.8]">{t('wo.messages.noMaterials')}</span>
                        </div>
                      )}
                      {/* عرض حالة الـ MRs المرتبطة */}
                      <div className="mt-6 border-t pt-4">
                        <h4 className="text-[0.9em] font-black text-ink-secondary uppercase mb-3 flex items-center gap-2">
                          <List size={14}/> {t('wo.messages.linkedMRs')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* لو عندك لستة بالـ MRs المفتوحة في الداتا */}
                          {pagedRows('related_mrs', data.related_mrs).map(({ row: mr, idx: i }) => (
                            <div key={i} className="bg-surface-card border-l-4 border-l-status-warning p-2 shadow-sm flex justify-between items-center">
                              <div>
                                <p className="text-[10px] font-black text-primary">{mr.mrnum}</p>
                                <p className="text-[9px] text-ink-secondary">{mr.description}</p>
                              </div>
                              <span className="bg-status-warning-bg text-status-warning px-2 py-0.5 rounded-full text-[8px] font-black">
                                {mr.status}
                              </span>
                            </div>
                          ))}
                        </div>
                        {rowPager('related_mrs', data.related_mrs)}
                      </div>
                      <button
                        onClick={() => addRow('actual_materials')}
                        className="mt-4 bg-status-success hover:bg-status-success text-white px-5 py-2 rounded-sm text-[10px] font-black uppercase shadow-sm flex items-center gap-2 transition-transform active:scale-95"
                      >
                        <Plus size={14} strokeWidth={3}/> {t('wo.actions.addMaterial')}
                      </button>
                    </div>
                  )}

                  {/* Actual Tools */}
                  {actualSubTab === 'Tools' && (
                    <div className="animate-in fade-in duration-300">
                      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-surface-panel text-ink-secondary text-[0.8] uppercase font-black border-b">
                          <tr><th className="p-3 text-left">{t('wo.tables.toolNum')}</th><th className="p-3 text-left">{t('wo.tables.description')}</th><th className="p-3 text-left w-40">{t('wo.tables.assetUnit')}</th><th className="p-3 text-center w-32">{t('wo.tables.hrs')}</th><th className="p-3 text-center w-32">{t('wo.tables.rate')}</th><th className="p-3 text-center w-32 bg-status-success-bg">{t('wo.tables.cost')}</th><th className="p-3 w-10"></th></tr>
                        </thead>
                        <tbody>
                          {pagedRows('actual_tools', data.actual_tools).map(({ row, idx }) => (
                            <tr key={idx} className="border-b border-line-subtle text-[10px]">
                              <td className="p-3 border-r font-bold flex justify-between uppercase">{row.toolnum || '---'}<Search size={14} className="cursor-pointer" onClick={() => handleSearch('row_act_tool', 'Tool', 'tools', idx)}/></td>
                              <td className="p-3 border-r text-ink-secondary font-semibold italic normal-case">{row.description}</td>
                              <td className="p-3 border-r">
                                <div className="flex justify-between items-center gap-1">
                                  <div className="flex flex-col">
                                    <span className="font-bold uppercase">{row.assetnum || '---'}</span>
                                    {row.assetnum && <span className="text-[8px] text-status-warning font-black uppercase normal-case">{row.asset_status || t('wo.tables.checkedOut')}</span>}
                                  </div>
                                  {row.toolnum
                                    ? <Search size={12} className="text-primary cursor-pointer shrink-0" onClick={() => handleSearch('row_act_asset', t('wo.tables.assetUnit'), 'assets', idx)}/>
                                    : <span className="text-[8px] text-ink-secondary italic normal-case shrink-0">{t('wo.tables.pickToolFirst')}</span>}
                                </div>
                              </td>
                              <td className="p-3 border-r text-center"><input type="number" className="w-16 text-center" value={row.hours} onChange={e => updateRow('actual_tools', idx, 'hours', e.target.value)} /></td>
                              <td className="p-3 border-r text-center"><input type="number" className="w-16 text-center font-mono" value={row.toolrate} onChange={e => updateRow('actual_tools', idx, 'toolrate', e.target.value)} /></td>
                              <td className="p-3 border-r text-center font-black text-status-success">{Number(row.linecost || 0).toLocaleString()}</td>
                              <td className="p-3 text-center"><Trash2 size={14} className="text-status-danger cursor-pointer" onClick={() => removeRow('actual_tools', idx)}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {rowPager('actual_tools', data.actual_tools)}
                      <button onClick={() => addRow('actual_tools')} className="mt-4 bg-status-success text-white px-6 py-2 rounded-sm text-[9px] font-black uppercase shadow-md"><Plus size={14}/> {t('wo.actions.addTool')}</button>
                    </div>
                  )}

                  {/* Actual Services */}
                  {actualSubTab === 'Services' && (
                    <div className="animate-in fade-in duration-300">
                      <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-surface-panel text-ink-secondary text-[0.8] uppercase font-black border-b">
                          <tr><th className="p-3 text-left">{t('wo.tables.serviceDesc')}</th><th className="p-3 text-left">{t('wo.tables.vendor')}</th><th className="p-3 text-center w-32 bg-status-success-bg">{t('wo.tables.cost')}</th><th className="p-3 text-center w-28">{t('wo.tables.purchaseRequest')}</th><th className="p-3 w-16"></th></tr>
                        </thead>
                        <tbody>
                          {pagedRows('actual_services', data.actual_services).map(({ row, idx }) => (
                            <tr key={idx} className="border-b text-[10px]">
                              <td className="p-3 border-r"><input className="w-full outline-none uppercase" value={row.description} onChange={e => updateRow('actual_services', idx, 'description', e.target.value)} /></td>
                              <td className="p-3 border-r font-black">
                                <div className="flex justify-between items-center">
                                  <div>
                                    <div>{row.vendor || '---'}</div>
                                    {row.vendor_name && <div className="text-[8px] italic text-status-warning font-normal normal-case">{row.vendor_name}</div>}
                                  </div>
                                  <Search size={14} className="cursor-pointer text-primary" onClick={() => handleSearch('row_act_service_vendor', 'Vendor', 'vendors', idx)} />
                                </div>
                              </td>
                              <td className="p-3 border-r text-center font-black text-status-success"><input type="number" className="w-24 text-center" value={row.linecost} onChange={e => updateRow('actual_services', idx, 'linecost', e.target.value)} /></td>
                              <td className="p-3 border-r text-center">
                                {row.mrnum ? (
                                  <span className="font-black text-status-warning text-[9px]">{row.mrnum}</span>
                                ) : (
                                  <button
                                    onClick={() => handleCreateServicePR(row, idx)}
                                    className="p-1 hover:bg-status-warning-bg rounded-full transition-colors group/mr"
                                    title={t('wo.actions.createMR')}
                                  >
                                    <FileText size={14} className="text-ink-secondary group-hover/mr:text-status-warning" />
                                  </button>
                                )}
                              </td>
                              <td className="p-3 text-center"><Trash2 size={14} className="text-status-danger cursor-pointer" onClick={() => removeRow('actual_services', idx)}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {rowPager('actual_services', data.actual_services)}
                      <button onClick={() => addRow('actual_services')} className="mt-4 bg-status-success text-white px-6 py-2 rounded-sm text-[9px] font-black uppercase shadow-md"><Plus size={14}/> {t('wo.actions.addService')}</button>
                    </div>
                  )}
                </div>
            
                {/* Summary Footer */}
                {/* إضافة قسم الـ Assignments في الفوتر لإظهار التكلفة المحجوزة */}
                {/* شريط ملخص التكاليف (Summary Footer) */}
                <div className="bg-primary-dark text-white p-5 flex justify-around items-center border-t border-white/10 shadow-2xl z-10">

                    {/* 1. تكلفة العمالة الفعلية */}
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-slate-400 font-bold uppercase mb-1 tracking-tighter">{t('wo.footer.totalLabor')}</span>
                        <span className="text-lg font-black text-blue-400">
                            {data.actual_labor?.reduce((sum, r) => sum + Number(r.linecost || 0), 0).toLocaleString()}
                        </span>
                    </div>

                    <div className="w-[1px] h-10 bg-white/10"></div>

                    {/* 2. تكلفة المواد الفعلية */}
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-slate-400 font-bold uppercase mb-1 tracking-tighter">{t('wo.footer.totalMaterial')}</span>
                        <span className="text-lg font-black text-orange-400">
                            {data.actual_materials?.reduce((sum, r) => sum + Number(r.linecost || 0), 0).toLocaleString()}
                        </span>
                    </div>

                    <div className="w-[1px] h-10 bg-white/10"></div>

                    {/* 3. التكلفة المحجوزة (Assignments) - باللون الأصفر كما اتفقنا */}
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-yellow-500/80 font-bold uppercase mb-1 tracking-tighter text-center">
                            {t('wo.footer.assigned')}
                        </span>
                        <span className="text-lg font-black text-yellow-500">
                            {data.assignments?.reduce((sum, r) => sum + Number(r.linecost || 0), 0).toLocaleString()}
                        </span>
                    </div>

                    <div className="w-[1px] h-10 bg-white/10"></div>

                    {/* 4. تكلفة الأدوات والخدمات (Other) */}
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-slate-400 font-bold uppercase mb-1 tracking-tighter">{t('wo.footer.toolsServ')}</span>
                        <span className="text-lg font-black text-purple-400">
                            {(
                                (data.actual_tools?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0) +
                                (data.actual_services?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0)
                            ).toLocaleString()}
                        </span>
                    </div>

                    <div className="w-[1px] h-10 bg-white/10"></div>

                    {/* 5. الإجمالي النهائي (Actual Order Cost) */}
                    <div className="flex flex-col items-center bg-status-success-bg/20 px-6 py-2 rounded-lg border border-status-success/30">
                        <span className="text-[9px] text-status-success font-bold uppercase mb-1 tracking-widest">{t('wo.footer.totalActual')}</span>
                        <span className="text-xl font-black text-status-success">
                            {(
                                (data.actual_labor?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0) +
                                (data.actual_materials?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0) +
                                (data.actual_tools?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0) +
                                (data.actual_services?.reduce((sum, r) => sum + Number(r.linecost || 0), 0) || 0)
                            ).toLocaleString()}
                            <span className="text-[10px] ml-1 uppercase">{currency}</span>
                        </span>
                    </div>
                </div>
            </div>
          )}
          {activeTab === 'Safety' && (
            <div className="max-w-7xl mx-auto space-y-6 animate-in slide-in-from-right duration-500 pb-20">
              
              {/* 1. Hazards & Precautions Section */}
              <Section title={t('wo.safety.hazardsTitle')}>
                <div className="bg-surface-card border rounded shadow-sm overflow-hidden">
                  <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                    <thead className="bg-surface-panel border-b text-ink text-[0.9] font-black uppercase">
                      <tr>
                        <th className="p-3 border-r w-40">{t('wo.safety.hazardCode')}</th>
                        <th className="p-3 border-r">{t('wo.safety.hazardDesc')}</th>
                        <th className="p-3">{t('wo.safety.precautions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.wohazards?.length > 0 ? (
                        pagedRows('wohazards', data.wohazards).map(({ row: h, idx: i }) => (
                          <tr key={i} className="hover:bg-status-danger-bg/30 transition-colors">
                            <td className="p-3 border-r font-black text-status-danger align-top flex items-center gap-2">
                              <AlertTriangle size={14} className="text-status-danger" /> {h.hazardnum}
                            </td>
                            <td className="p-3 border-r font-semibold text-ink align-top uppercase italic">
                              {h.description}
                            </td>
                            <td className="p-0 align-top">
                              {/* عرض الاحتياطات المربوطة بهذا الخطر بالذات */}
                              <table className="w-full bg-surface-panel/50">
                                <tbody>
                                  {h.precautions?.map((p, pi) => (
                                    <tr key={pi} className="border-b last:border-0 border-line-subtle">
                                      <td className="p-2 flex items-start gap-2">
                                        <div className="mt-1"><CheckCircle size={12} className="text-status-success" /></div>
                                        <div>
                                          <span className="font-black text-primary text-[0.8] mr-2">{p.precautionnum}</span>
                                          <span className="text-ink font-bold uppercase">{p.description}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="p-10 text-center text-ink-secondary italic">
                            {t('wo.safety.noHazards')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {rowPager('wohazards', data.wohazards)}
              </Section>

              {/* 2. Lock Out / Tag Out Section */}
              <Section title={t('wo.safety.lotoTitle')}>
                <div className="bg-surface-card border rounded shadow-sm overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-surface-panel border-b text-ink text-[0.9] font-black uppercase tracking-tighter">
                      <tr>
                        <th className="p-3 border-r w-20 text-center">{t('wo.safety.step')}</th>
                        <th className="p-3 border-r w-40">{t('wo.safety.tagNum')}</th>
                        <th className="p-3 border-r">{t('wo.safety.isoDesc')}</th>
                        <th className="p-3 border-r w-32 text-center">{t('wo.safety.reqState')}</th>
                        <th className="p-3 w-32 text-center">{t('wo.safety.completed')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {data.woloto?.length > 0 ? (
                        pagedRows('woloto', data.woloto).map(({ row: t, idx: i }) => (
                          <tr key={i} className="hover:bg-primary-soft/30 transition-colors font-bold uppercase">
                            <td className="p-3 border-r text-center text-ink-secondary font-black">{t.sequence || i+1}</td>
                            <td className="p-3 border-r font-black text-primary">{t.tagoutnum}</td>
                            <td className="p-3 border-r text-ink">{t.description}</td>
                            <td className="p-3 border-r text-center">
                              <span className="bg-status-danger-bg text-status-danger px-3 py-1 rounded-full text-[0.8] font-black">
                                {t.required_state || 'LOCKED/OFF'}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {/* هنا ممكن الفني يعلم إنه خلص خطوة العزل فعلياً */}
                              <input type="checkbox" className="w-4 h-4 accent-status-success" />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="p-10 text-center text-ink-secondary italic">
                            {t('wo.safety.noLoto')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {rowPager('woloto', data.woloto)}
              </Section>

              {/* 3. Hazardous Materials Section (Optional but Professional) */}
              <Section title="Hazardous Materials Information">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {data.wohazards_materials?.length > 0 ? (
                      pagedRows('wohazards_materials', data.wohazards_materials).map(({ row: m, idx: i }) => (
                        <div key={i} className="flex items-center gap-4 p-4 border rounded-sm bg-status-warning-bg/50 border-status-warning/20">
                          <div className="p-3 bg-status-warning text-white rounded-sm"><Zap size={20}/></div>
                          <div className="flex flex-col">
                            <span className="font-black text-status-warning">{m.itemnum}</span>
                            <span className="text-[10px] text-status-warning font-bold uppercase">{m.description}</span>
                            <span className="text-[9px] mt-1 text-ink-secondary italic">Precautions: {m.special_precautions || 'Follow Standard SDS'}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-2 p-6 text-center text-ink-secondary border-2 border-dashed rounded italic">
                        No hazardous materials associated with this task.
                      </div>
                    )}
                </div>
                {rowPager('wohazards_materials', data.wohazards_materials)}
              </Section>
            </div>
          )}
          {activeTab === 'Failure Reporting' && (
            <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-20">

              {/* الهيدر التعريفي */}
              <div className="bg-primary-dark text-white p-4 rounded-sm flex justify-between items-center shadow-lg">
                <div className="flex items-center gap-4">
                  <BrainCircuit size={28} className="text-status-warning" />
                  <div>
                    <h3 className="font-black uppercase text-sm tracking-widest">{t('wo.failure.title')}</h3>
                    <p className="text-[0.9] opacity-70">{t('wo.failure.currClass')}: <span className="text-status-warning font-black">{data.failure_class || 'NOT DEFINED'}</span></p>
                  </div>
                </div>
              </div>

              {/* 1. PROBLEMS TABLE */}
              {/* 🎯 لازم نحافظ على الإندكس الحقيقي بتاع كل صف جوه data.wofailure
              الكاملة (مش إندكس الفلترة) - وإلا updateFailureRow/removeFailureRow
              هيعدلوا/يحذفوا صف غلط أول ما يبقى في أكتر من نوع (Problem+Cause مثلاً) */}
              <FailureTable
                title={t('wo.failure.problems')}
                data={data.wofailure.map((f, idx) => ({ ...f, __idx: idx })).filter(f => f.failure_type === 'PROBLEM')}
                onAdd={() => addFailureRow('PROBLEM')}
                onRemove={removeFailureRow}
                onSearch={(idx) => handleSmartSearch('failure_code', 'Select Problem', 'LOCAL_LIST', (row) => {
                    updateFailureRow(idx, 'failure_code', row.code, row.description);
                }, failureLists.problems)}
              />

              {/* 2. CAUSES TABLE */}
              <FailureTable
                title={t('wo.failure.causes')}
                data={data.wofailure.map((f, idx) => ({ ...f, __idx: idx })).filter(f => f.failure_type === 'CAUSE')}
                onAdd={() => addFailureRow('CAUSE')}
                onRemove={removeFailureRow}
                onSearch={(idx) => handleSmartSearch('failure_code', 'Select Cause', 'LOCAL_LIST', (row) => {
                    updateFailureRow(idx, 'failure_code', row.code, row.description);
                }, failureLists.causes)}
              />

              {/* 3. REMEDIES TABLE */}
              <FailureTable
                title={t('wo.failure.remedies')}
                data={data.wofailure.map((f, idx) => ({ ...f, __idx: idx })).filter(f => f.failure_type === 'REMEDY')}
                onAdd={() => addFailureRow('REMEDY')}
                onRemove={removeFailureRow}
                onSearch={(idx) => handleSmartSearch('failure_code', 'Select Remedy', 'LOCAL_LIST', (row) => {
                    updateFailureRow(idx, 'failure_code', row.code, row.description);
                }, failureLists.remedies)}
              />
            </div>
          )}
      </div>
    </div>
  );
};

// --- Reusable UI Components ---
const TAB_ROWS_PER_PAGE = 5;

// 🎯 شريط صفحات صغير تحت جداول التابات - مابيظهرش غير لو الصفوف أكتر من 5.
// (إضافة صف جديد بتنقل لآخر صفحة من showLastRowPage في دوال الإضافة)
const RowPager = ({ total, page, onPage, isRTL }) => {
  const pages = Math.max(1, Math.ceil(total / TAB_ROWS_PER_PAGE));
  const current = Math.min(page, pages);

  if (total <= TAB_ROWS_PER_PAGE) return null;
  const from = (current - 1) * TAB_ROWS_PER_PAGE + 1;
  const to = Math.min(current * TAB_ROWS_PER_PAGE, total);
  const btn = "p-1 rounded-sm border border-line hover:bg-primary-soft hover:text-primary disabled:opacity-30 disabled:hover:bg-transparent";
  return (
    <div className="flex items-center justify-end gap-2 mt-2 text-[10px] font-black text-ink-secondary">
      <span className="font-mono" dir="ltr">{from}–{to} / {total}</span>
      <button type="button" className={btn} disabled={current <= 1} onClick={() => onPage(current - 1)}>
        <ChevronLeft size={14} className={isRTL ? 'rotate-180' : ''} />
      </button>
      <span className="font-mono" dir="ltr">{current} / {pages}</span>
      <button type="button" className={btn} disabled={current >= pages} onClick={() => onPage(current + 1)}>
        <ChevronRight size={14} className={isRTL ? 'rotate-180' : ''} />
      </button>
    </div>
  );
};

const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return dateString.split('.')[0].substring(0, 16);
  };
const Section = ({ title, children }) => (
  <div className="bg-surface-card border border-line rounded shadow-sm">
    <div className="p-2 bg-surface-panel border-b border-line font-bold text-ink-secondary uppercase text-[0.8] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-primary rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const DataField = ({ label, value, desc, onSearch, onMouseEnter, onMouseLeave }) => (
  <div className="flex flex-col border-b border-line-subtle py-2 group hover:bg-primary-soft transition-all px-1">
    <label className="text-[0.8] font-black text-ink uppercase tracking-tighter">{label}</label>
    <div className="flex justify-between items-start mt-1">
      <div className="flex flex-col">
        <span
          className={`font-black text-primary uppercase ${onMouseEnter ? 'cursor-help hover:text-primary-hover' : ''}`}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {value || '---'}
        </span>
        {/* الوصف تحت الكود مباشرة - نفس الأسلوب المستخدم في شاشة الأصول */}
        {desc && <span className="text-status-warning font-normal italic text-[10px]">{desc}</span>}
      </div>
      {onSearch && <Search size={13} className="text-primary-hover cursor-pointer hover:text-primary transition-colors mt-0.5 shrink-0" onClick={onSearch} />}
    </div>
  </div>
);

const DateField = ({ label, value, onChange, hasCalendar, readOnly }) => (
  <div className="flex flex-col border-b border-line py-1 group focus-within:border-primary transition-all">
    <div className="flex justify-between items-center">
       <label className="text-[0.8] font-black text-ink uppercase tracking-tighter">{label}</label>
       {hasCalendar && <Calendar size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-all cursor-pointer"/>}
    </div>
    <input
      type="datetime-local"
      readOnly={readOnly}
      // إضافة CSS كلاس عشان اليوزر يعرف إن الحقل مقفول
      className={`outline-none font-bold py-0.5 text-[0.8] bg-transparent ${readOnly ? 'text-ink-secondary cursor-not-allowed' : 'text-primary'}`}
      // ?? ??????? ????? ??? ??? ?? ???? ??? input
      value={formatDateTime(value)}
      onChange={e => !readOnly && onChange(e.target.value)}
    />
  </div>
);

const EditableField2 = ({ label, value, onChange, onSearch, isRequired, hasList, hasCalendar, desc, type = "text" }) => (
  <div className="flex flex-col gap-1 border-b border-line group">
    <label className={`${isRequired ? 'text-status-danger' : 'text-ink'} font-bold text-[0.8] uppercase tracking-tighter`}>{label}</label>
    <div className="flex items-center justify-between pb-1">
      <input
        type={type} // <--- ????? ?? ???? ?????? ????? (text, datetime-local, number, etc.)
        className="outline-none font-bold text-primary bg-transparent w-full custom-datetime-input"
        value={value || ''}
        onChange={e => onChange && onChange(e.target.value)}
      />
      {desc && <span className="text-ink font-normal px-4 italic whitespace-nowrap">{desc}</span>}
      <div className="flex gap-2 text-primary-hover cursor-pointer">
        {hasList && <List size={14}/>}
        {hasCalendar && <Calendar size={14}/>}
        {onSearch && <Search size={13} className="text-primary-hover cursor-pointer hover:text-primary transition-colors" onClick={onSearch} />}
      </div>
    </div>
  </div>
);
const EditableField = ({ label, value, onChange, onSearch, isRequired, lookupType, hasList, hasCalendar, desc, type = "text" }) => {
  const [description, setDesc] = useState("");
  // 🎯 استيت محلي عشان اليوزر يكتب براحته بدون تدخل التنسيق المالي أثناء الكتابة
  const [inputValue, setInputValue] = useState(value || "");

  // تحديث الاستيت المحلي لو الـ value اللي جاية من بره اتغيرت (عشان الـ Load)
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // منطق جلب الوصف (Lookup Description)
  useEffect(() => {
    if (value && lookupType) {
      const fetchDesc = async () => {
        try {
          const res = await axios.get(`/api/lookup-desc/resolve?type=${lookupType}&code=${value}`);
          setDesc(res.data.description || "");
        } catch (err) { setDesc(""); }
      };
      fetchDesc();
    } else { setDesc(""); }
  }, [value, lookupType]);

  // دالة التنسيق المالي (بتشتغل فقط عند العرض)
  const formatCurrency = (val) => {
    if (val === "" || val === null || isNaN(val)) return val;
    return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // هل الحقل ده مالي؟ (Price أو Cost)
  const isNumericField = label.toLowerCase().includes("price") || label.toLowerCase().includes("cost") || label.toLowerCase().includes("rate");

  return (
    <div className="flex flex-col gap-1 border-b border-line group mb-2">
      <label className={`${isRequired ? 'text-status-danger' : 'text-ink-secondary'} font-bold text-[0.9] uppercase tracking-tighter`}>
        {label}
      </label>

      <div className="flex items-center justify-between pb-0.5">
        <input
          type={type === "datetime-local" ? "datetime-local" : "text"}
          className="outline-none font-black text-primary bg-transparent w-full text-xs"
          // 🎯 لو حقل مالي، اعرض القيمة الخام أثناء الكتابة، والمعادلة (الفواصل) وأنت بره
          value={type === "datetime-local" && inputValue ? inputValue.substring(0, 16).replace(' ', 'T') : inputValue}
          
          onChange={e => {
            let val = e.target.value;
            if (isNumericField) {
              // السماح فقط بالأرقام والنقطة العشرية (تنظيف أي فواصل جاية بالغط)
              val = val.replace(/,/g, '');
              if (val !== "" && isNaN(val) && val !== ".") return;
            }
            setInputValue(val); // تحديث المنظر قدام اليوزر فوراً
            if (!isNumericField) onChange(val); // لو مش مالي سيف فوراً
          }}

          onBlur={() => {
            // 🎯 لما اليوزر يخرج من الخانة، نبعت القيمة النهائية للـ Parent وننظفها
            if (isNumericField) {
              const cleanVal = String(inputValue).replace(/,/g, '');
              onChange(cleanVal);
              // نحول المنظر للفواصل بعد الخروج
              setInputValue(formatCurrency(cleanVal));
            }
          }}

          onFocus={() => {
            // 🎯 لما يرجع يضغط، نشيل الفواصل عشان يعرف يمسح ويعدل بسهولة
            if (isNumericField) {
              setInputValue(String(inputValue).replace(/,/g, ''));
            }
          }}
        />
        
        <div className="flex gap-2 text-primary-hover cursor-pointer shrink-0">
          {hasList && <List size={14}/>}
          {hasCalendar && <Calendar size={14}/>}
          {onSearch && <Search size={13} className="text-primary-hover hover:text-primary transition-colors" onClick={onSearch} />}
        </div>
      </div>

      {(description || desc) && (
        <div className="text-[10px] text-status-warning font-bold italic animate-in fade-in slide-in-from-left-1 leading-none pb-1">
          {description || desc}
        </div>
      )}
    </div>
  );
};

const Table = ({ columns, data, isActual }) => (
  <div className="border border-line rounded-sm overflow-hidden">
    <table className="w-full text-left">
      <thead className={`bg-surface-panel border-b border-line text-[0.9] font-black uppercase text-ink`}>
        <tr>
          {columns.map(col => <th key={col} className="p-2 border-r border-line">{col}</th>)}
          <th className="p-2 w-8"></th>
        </tr>
      </thead>
      <tbody>
        {data?.length === 0 ? (
          <tr><td colSpan={columns.length + 1} className="p-8 text-center text-ink-secondary italic">No records to display</td></tr>
        ) : data.map((row, i) => (
          <tr key={i} className="border-b border-line-subtle text-[10px] hover:bg-primary-soft transition-colors">
            {Object.values(row).slice(1, columns.length + 1).map((val, j) => <td key={j} className="p-2 border-r border-line-subtle font-bold text-primary">{val}</td>)}
            <td className="p-2 text-center"><Trash2 size={12} className="text-ink-secondary hover:text-status-danger cursor-pointer"/></td>
          </tr>
        ))}
      </tbody>
    </table>
    <button className={`m-2 px-4 py-1.5 rounded-sm text-[9px] font-bold flex items-center gap-2 text-white shadow-sm ${isActual ? 'bg-status-success' : 'bg-primary'}`}>
      <Plus size={12}/> New Row
    </button>
  </div>
);

const FailureTable = ({ title, data, onAdd, onSearch, onRemove }) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(data.length / TAB_ROWS_PER_PAGE));
  const start = (Math.min(page, pages) - 1) * TAB_ROWS_PER_PAGE;
  const visibleRows = data.slice(start, start + TAB_ROWS_PER_PAGE);
  const handleAdd = () => { onAdd(); setPage(Number.MAX_SAFE_INTEGER); };
  return (
  <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden">
    <div className="p-3 bg-surface-panel border-b border-line flex justify-between items-center">
      <h4 className="text-[0.9] font-black text-ink-secondary uppercase tracking-tighter flex items-center gap-2">
        <Filter size={14} className="text-primary" /> {title}
      </h4>
      <span className="text-[0.8] bg-surface-panel px-2 py-0.5 rounded-full font-bold border border-line">{data.length} {t('wo.failure.records')}</span>
    </div>
    <table className="w-full text-left border-collapse">
      <thead className="bg-surface-panel text-ink-secondary font-bold uppercase border-b border-line">
        <tr>
          <th className="p-3 border-r border-line w-48">{t('wo.tables.failCode')}</th>
          <th className="p-3 border-r border-line">{t('wo.tables.description')}</th>
          <th className="p-3 w-10 text-center"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-line-subtle">
        {visibleRows.map((row, i) => (
          <tr key={row.__idx ?? start + i} className="hover:bg-primary-soft transition-all">
            <td className="p-3 border-r border-line-subtle font-black text-primary flex justify-between items-center group">
              {row.failure_code || '---'}
              <Search size={14} className="text-primary-hover cursor-pointer hover:text-primary" onClick={() => onSearch(row.__idx ?? start + i)} />
            </td>
            <td className="p-3 border-r border-line-subtle text-ink-secondary italic">{row.description}</td>
            <td className="p-3 text-center">
              <button onClick={() => onRemove(row.__idx ?? start + i)} className="text-ink-secondary hover:text-status-danger"><Trash2 size={14}/></button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="px-3">
      <RowPager total={data.length} page={page} onPage={setPage} isRTL={i18n.language === 'ar' || i18n.language === 'ur'} />
    </div>
    <button onClick={handleAdd} className="m-3 bg-primary text-white px-6 py-1.5 rounded-sm font-black text-[10px] flex items-center gap-2 hover:bg-primary-hover transition-all shadow-md uppercase">
      <Plus size={14} /> {t('common.new_row')}
    </button>
  </div>
)}; 

export default WorkOrderDetails;